import { Api, deleteEverything, DuplicateRef, DuplicateStatus, GalleryImage, Item, ItemCrop, LoadProgress, McpCredential, Note, NoteLevel, Position, Room, ShopLink, ReferenceConfig, fetchReferenceSnapshot, inParallel, onWorkChange, setLoadProgress, track, WorkState, workState } from "./Api.js";
import * as Views from "./views.js";
import { buildLocalSnapshot, CompareResult, diffSnapshots, Snapshot, SNAPSHOT_SCHEMA } from "./compare.js";

type ViewName = "dashboard" | "positions" | "review" | "rooms" | "room" | "position" | "form" | "export" | "duplicates" | "notes" | "search" | "items" | "mcp" | "compare";

const VIEW_NAMES: ViewName[] = ["dashboard", "positions", "review", "rooms", "room", "position", "form", "export", "duplicates", "notes", "search", "items", "mcp", "compare"];

/** Steuerung des Lightbox-Zooms, siehe attachZoom(). */
interface ZoomControl {
    zoomBy(factor: number): void;
    reset(): void;
    scale(): number;
    min: number;
    max: number;
}

/** Ein Bild in der Lightbox-Gruppe, aus dem auslösenden Element gelesen. */
interface LightboxEntry {
    /** Volles Foto — auch bei Ausschnitten, für "Original öffnen". */
    url: string;
    alt: string;
    /** Quelle für den Zuschnitt (große Variante statt Vorschaugröße). */
    cropSrc: string;
    crop: ItemCrop | null;
    /** "item" | "position" | "room" — bestimmt, welche Daten die Tafel zeigt. */
    kind: string | null;
    id: number;
    /** Attachment-ID des Fotos (0 = unbekannt) — ordnet ihm die Ausschnitte zu. */
    media: number;
}

/** Merkt, ob die Datentafel der Lightbox eingeblendet war. Standard: aus. */
const LIGHTBOX_INFO_KEY = "hausinventar.lightbox.info";
/**
 * Merkt, ob die Ausschnitte auf Positionsfotos eingezeichnet werden.
 * Standard: an — genau dafür öffnet man das Positionsfoto meist.
 */
const LIGHTBOX_MARKS_KEY = "hausinventar.lightbox.ausschnitte";
/** Name des Versicherungsnehmers für den Kopf der Schadenaufstellung. */
const EXPORT_OWNER_KEY = "hausinventar.export.owner";
/** Ort für die Unterschriftszeile am Fuß der Schadenaufstellung. */
const EXPORT_PLACE_KEY = "hausinventar.export.place";
/** Bearbeiteter Erläuterungstext, der der Aufstellung vorangestellt wird. */
const EXPORT_INTRO_KEY = "hausinventar.export.intro";
/**
 * Zugang zur Referenz-Instanz der Vergleichsansicht (Adresse, Benutzer,
 * Anwendungspasswort).
 *
 * Bewusst im Browser und nicht auf dem Server: Abgefragt wird die Referenz
 * vom Browser aus, das Passwort müsste also ohnehin hier vorliegen — eine
 * zusätzliche Kopie in der Datenbank machte es nur an einer Stelle mehr
 * angreifbar. Die Ansicht sagt offen, dass es hier liegt, und hat einen Knopf,
 * der es wieder vergisst.
 */
const COMPARE_REFERENCE_KEY = "hausinventar.vergleich.referenz";
/** Zeigt die Vergleichsansicht auch Reihenfolge und Import-Protokolle? */
const COMPARE_MINOR_KEY = "hausinventar.vergleich.nebensachen";

/**
 * Frei vergebener Name dieser Instanz, im Kopf der Seitenleiste bearbeitbar.
 * Pro Browser-Ursprung gespeichert — trifft also ohnehin nur die jeweilige
 * Installation und hilft, mehrere gleichzeitig geöffnete Umgebungen
 * (Entwicklung, Produktion, weitere Haushalte) auseinanderzuhalten.
 */
const INSTANCE_LABEL_KEY = "hausinventar.instance.label";

/**
 * Vorbelegung der Erläuterung, die der Aufstellung vorangestellt werden kann.
 *
 * Der Text geht wörtlich an die Versicherung und ist deshalb in der Sprache des
 * Versicherungsnehmers geschrieben — kein Verweis auf Werkzeuge oder darauf, wie
 * die Liste technisch entstanden ist.
 *
 * Er erläutert ausschließlich das, was der Empfänger vor sich hat: die sechs
 * Spalten des Ausdrucks und den Maßstab, nach dem die Beträge gebildet sind.
 * Alles Übrige — Fotos, Wertbegründungen, Preisfundstellen — steht im eigenen
 * Abschnitt "Unterlagen" und ist dort ausdrücklich als *nicht* Bestandteil
 * dieser Blätter bezeichnet. Der Ausdruck enthält nichts davon, und ein Text,
 * der darauf verweist, als stünde es daneben, führt den Sachbearbeiter in die
 * Irre.
 *
 * Zeilen mit "# " am Anfang werden als Zwischenüberschrift gesetzt, eine
 * Leerzeile trennt Absätze. Der Text ist im Export-Formular frei überschreibbar.
 */
const DEFAULT_EXPORT_INTRO = `# Zu diesem Dokument

Die folgenden Blätter verzeichnen den Hausrat, der bei dem Schadenereignis zerstört oder beschädigt wurde. Die Aufnahme erfolgte raumweise; die Blätter sind durchgezählt, die letzte Zeile nennt den Gesamtwert. Alle Beträge sind Euro-Beträge einschließlich Mehrwertsteuer.

# Die Spalten

Pos. — Nummer der Aufnahme, zu der der Gegenstand gehört. Alle Gegenstände einer Aufnahme tragen dieselbe Nummer und stehen untereinander.

Anzahl — Stückzahl. Sie ist ausgezählt, wo die Stücke einzeln unterscheidbar waren. Bei großen gleichartigen Mengen ist an einem Teilbereich gezählt und auf den Gesamtbestand hochgerechnet worden.

Gegenstand — Bezeichnung. Gleichartige Ware, die sich nicht sinnvoll trennen ließ, steht in einer Zeile zusammengefasst.

Sonstiges — Merkmale, an denen der Gegenstand zu erkennen ist: Marke, Aufdruck, Größe, Zusammensetzung einer zusammengefassten Zeile.

# Grundlage der Beträge

Angegeben ist der Betrag, der aufzuwenden ist, um einen gleichwertigen Gegenstand heute neu zu beschaffen — entsprechend der vereinbarten Entschädigung zum Neuwert. Ein Abschlag für Alter oder Gebrauchsspuren ist darin nicht enthalten.

Wo sich ein Gegenstand eindeutig bestimmen ließ, beruht der Betrag auf einem Angebot des Handels. Wo Marke oder Modell nicht festzustellen waren, ist er anhand vergleichbarer Ware geschätzt.

Lagen für dasselbe Produkt mehrere Angebote vor, ist der obere Rand der Spanne angesetzt: Eine Verpflichtung, beim günstigsten Anbieter zu kaufen, besteht nicht. Aktions- und Restpostenpreise sind nicht verwendet worden. Ist ein Modell nicht mehr erhältlich, gilt der Preis des heute vergleichbaren Nachfolgers.

Versandkosten sind in den Beträgen nicht enthalten.

# Zur Spalte „Alter“

Die Altersangaben sind Schätzungen. Sie stützen sich, soweit möglich, auf das Erscheinungsjahr des jeweiligen Modells, sonst auf Bauform, Aufdrucke, Anschlüsse oder den Zusammenhang, in dem der Gegenstand angeschafft wurde. Angegeben ist das Alter des Gegenstands selbst, nicht die Dauer, die er sich im Haushalt befand — bei geerbten und gebraucht erworbenen Stücken fällt beides auseinander.

Die Angabe ist als Anhaltspunkt für die Zeitwertermittlung gedacht. Auf die genannten Beträge wirkt sie sich nicht aus; diese bleiben Neuwerte.

# Sammler- und Liebhaberstücke

Ein Teil des Bestands ist neu nicht mehr erhältlich und wird nur noch unter Sammlern gehandelt. Bei diesen Stücken ist ein altersbedingter Abschlag nicht sachgerecht — sie verlieren mit den Jahren nicht an Wert. Der genannte Betrag ist dort bereits der Preis, der für die Wiederbeschaffung auf dem Sammlermarkt aufzuwenden ist; er entspricht zugleich dem Zeitwert.

# Jeder Gegenstand nur einmal

Da raumweise fotografiert wurde, sind einzelne Gegenstände auf mehreren Aufnahmen zu sehen. Solche Doppelungen sind vor dem Ausdruck entfernt worden: Jeder Gegenstand steht in dieser Aufstellung genau einmal, und der Gesamtwert enthält ihn genau einmal.

# Nicht bestimmbare Gegenstände

Gegenstände, die verdeckt lagen oder deren Beschriftung nicht zu entziffern war, sind als solche benannt und mit dem Wert vergleichbarer Ware angesetzt. Sie sind nicht weggelassen worden: Sie waren vorhanden und sind mit untergegangen.

# Unterlagen zu dieser Aufstellung

Nicht Bestandteil dieser Blätter, aber vorhanden und auf Anforderung zu übergeben:

Die Aufnahmen, auf denen die Gegenstände zu sehen sind — eine je Position, dazu der Ausschnitt, der den einzelnen Gegenstand darin zeigt.

Zu jedem Gegenstand die Begründung seines Betrags und die Angabe, worauf er beruht: Kaufbeleg, Rechnung, ein Angebot des Handels oder Schätzung.

Zu jedem online ermittelten Preis die Fundstelle und das Datum der Abfrage. Die Preise sind über mehrere Tage ermittelt worden.

# Vollständigkeit

Die Aufstellung ist nach bestem Wissen erstellt. Wo eine Angabe unsicher geblieben ist, wurde das festgehalten, statt sie wegzulassen. Für Rückfragen zu einzelnen Positionen stehe ich zur Verfügung.`;
/** Je Liste, ob nur der sichtbare Ausschnitt gerendert wird. */
const VIRTUAL_KEY = "hausinventar.liste.virtuell.";
/** Je Liste die gespeicherte Spaltensortierung, als "spalte:richtung". */
const SORT_KEY = "hausinventar.liste.sortierung.";

/**
 * Voreinstellung der Virtualisierung je Liste.
 *
 * Nur die Gegenstandsliste ist standardmäßig virtualisiert: Sie hat knapp
 * zweitausend Zeilen mit Bildausschnitt und ist ohne sie spürbar zäh.
 * Positionen (rund 340) und Räume (15) laufen auch vollständig gerendert
 * flüssig — dort ist das vollständige DOM sogar praktischer, weil die
 * Suchfunktion des Browsers (Strg+F) alle Zeilen findet.
 */
const VIRTUAL_DEFAULT: Record<Views.VirtualList, boolean> = {
    items: true,
    positions: false,
    rooms: false,
};

/**
 * Zugriff auf localStorage gekapselt: In privaten Fenstern und bei
 * blockierten Cookies wirft schon das Lesen. Dann gilt eben der Standard und
 * die Einstellung überlebt die Sitzung nicht.
 */
function readStored(key: string): string {
    try {
        return window.localStorage.getItem(key) ?? "";
    } catch {
        return "";
    }
}

function writeStored(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Absichtlich ignoriert — siehe readStored.
    }
}

/** Hinterlegter Referenz-Zugang, oder null wenn keiner (oder ein kaputter) da ist. */
function readReference(): ReferenceConfig | null {
    try {
        const roh = JSON.parse(readStored(COMPARE_REFERENCE_KEY) || "null");
        if (!roh || typeof roh.url !== "string" || !roh.url) return null;
        return { url: roh.url, user: String(roh.user || ""), secret: String(roh.secret || "") };
    } catch {
        return null;
    }
}

function readVirtualPreference(list: Views.VirtualList): boolean {
    const gespeichert = readStored(VIRTUAL_KEY + list);
    return gespeichert === "" ? VIRTUAL_DEFAULT[list] : gespeichert === "1";
}

/**
 * Liest die gespeicherte Spaltensortierung einer Liste und validiert sie
 * gegen die erlaubten Spalten — ein veralteter Eintrag (etwa nach dem
 * Umbenennen einer Spalte) fällt still auf den Standard zurück.
 */
function readSortPreference<K extends string>(
    list: string,
    allowed: readonly K[],
    fallback: { key: K; dir: "asc" | "desc" }
): { key: K; dir: "asc" | "desc" } {
    const [key, dir] = readStored(SORT_KEY + list).split(":");
    return (allowed as readonly string[]).includes(key) && (dir === "asc" || dir === "desc")
        ? { key: key as K, dir }
        : fallback;
}

function writeSortPreference(list: string, sort: { key: string; dir: string }): void {
    writeStored(SORT_KEY + list, `${sort.key}:${sort.dir}`);
}

function readInfoPreference(): boolean {
    return readStored(LIGHTBOX_INFO_KEY) === "1";
}

function writeInfoPreference(on: boolean): void {
    writeStored(LIGHTBOX_INFO_KEY, on ? "1" : "0");
}

function readMarksPreference(): boolean {
    return readStored(LIGHTBOX_MARKS_KEY) !== "0";
}

function writeMarksPreference(on: boolean): void {
    writeStored(LIGHTBOX_MARKS_KEY, on ? "1" : "0");
}

/**
 * Liest die Shop-Link-Zeilen eines Formulars.
 *
 * Alle Zeilen benennen ihre drei Felder gleich — die Werte aus `getAll()`
 * stehen deshalb zeilenweise an derselben Stelle. Zeilen ohne URL sind leere
 * Eingabezeilen und fallen weg; ein Angebot ohne Preis oder Datum bleibt
 * dagegen erhalten: unvollständig, aber nicht wertlos.
 */
function readShopLinks(data: FormData): ShopLink[] {
    const urls = data.getAll("shop_url").map((v) => String(v).trim());
    const preise = data.getAll("shop_preis").map((v) => String(v).trim());
    const daten = data.getAll("shop_datum").map((v) => String(v).trim());

    return urls
        .map((url, index) => {
            const preis = Number((preise[index] ?? "").replace(",", "."));
            return {
                url,
                preis: Number.isFinite(preis) && preis > 0 ? preis : 0,
                datum: daten[index] ?? "",
            };
        })
        .filter((link) => link.url !== "");
}

/**
 * Liest die Verweiszeilen des Duplikat-Felds.
 *
 * Wie bei den Shop-Links stehen die gleichnamigen Felder aller Zeilen
 * positionsgleich in `getAll()`. Zeilen ohne gewähltes Ziel fallen weg: Ein
 * Verweis, der auf nichts zeigt, wäre nicht überprüfbar — und genau die
 * Überprüfbarkeit ist der Zweck des Feldes.
 */
function readDuplicateRefs(data: FormData): DuplicateRef[] {
    const items = data.getAll("dup_item").map((v) => Number(v) || 0);
    const positions = data.getAll("dup_position").map((v) => Number(v) || 0);
    const notes = data.getAll("dup_note").map((v) => String(v).trim());

    return items
        .map((item, index) => ({ item, position: positions[index] ?? 0, note: notes[index] ?? "" }))
        .filter((ref) => ref.item > 0 || ref.position > 0);
}

/**
 * Hält nur den sichtbaren Ausschnitt einer Tabelle im DOM.
 *
 * Die Gegenstandsliste hat knapp zweitausend Zeilen mit Bildausschnitt; als
 * vollständiges DOM kostet allein das Einsetzen mehrere hundert Millisekunden
 * und jedes Scrollen bleibt zäh. Gerendert wird deshalb nur der Bereich um das
 * Sichtfenster, oben und unten gehalten von je einer Abstandszeile, die die
 * ausgelassenen Zeilen in Pixeln vertritt. Für die Bildlaufleiste sieht die
 * Tabelle dadurch unverändert vollständig aus.
 *
 * Zeilenhöhen sind nicht einheitlich — eine lange Bezeichnung bricht um, eine
 * aufgeklappte Position bringt eine ganze Unterzeile mit. Jede Zeile bringt
 * deshalb ihre erwartete Höhe mit (siehe VirtualRow in views.ts); gemessen und
 * korrigiert wird, sobald sie das erste Mal wirklich im Layout steht.
 *
 * Beim Scrollen werden **nur die Zeilen am Rand des Fensters** ein- und
 * ausgehängt. Den ganzen Rumpf neu zu schreiben wäre einfacher, flackert aber
 * sichtbar: Jede Zeile verlöre dabei ihren bereits zugeschnittenen
 * Bildausschnitt und müsste ihn neu aufbauen, obwohl sich an ihr nichts
 * geändert hat.
 */
class VirtualTable {
    /** Aktuell angenommene Höhe je Zeile — anfangs die Schätzung der Ansicht. */
    private readonly heights: number[];
    /** Summierte Höhen, offsets[i] = Abstand der Zeile i vom Tabellenanfang. */
    private offsets: number[] = [];
    private frame = 0;
    private von = 0;
    private bis = -1;
    /**
     * Die aktuell eingehängten Zeilen. Ein Eintrag kann zwei <tr> umfassen —
     * eine aufgeklappte Position bringt ihre Unterzeile mit.
     */
    private readonly rendered = new Map<number, HTMLElement[]>();
    private readonly topSpacer: HTMLTableRowElement;
    private readonly bottomSpacer: HTMLTableRowElement;
    private readonly onScroll = () => this.schedule();

    /**
     * Wird vor dem Aushängen von Zeilen gerufen. Wer sich an sie gehängt hat
     * — etwa der Beobachter der Bildausschnitte —, kann hier loslassen.
     */
    onRowsRemoved: ((elemente: HTMLElement[]) => void) | null = null;

    constructor(
        private readonly scroller: HTMLElement,
        private readonly tbody: HTMLElement,
        private readonly rows: Views.VirtualRow[],
        private readonly columns: number,
        private readonly afterDraw: (neu: HTMLElement[]) => void
    ) {
        this.heights = rows.map((r) => r.hoehe);
        this.recalc();
        this.topSpacer = this.makeSpacer();
        this.bottomSpacer = this.makeSpacer();
        this.tbody.replaceChildren(this.topSpacer, this.bottomSpacer);
        this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
        this.draw();
    }

    destroy(): void {
        this.scroller.removeEventListener("scroll", this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
    }

    /** Scrollposition des Containers — für das Merken beim Ansichtswechsel. */
    get scrollTop(): number {
        return this.scroller.scrollTop;
    }

    set scrollTop(value: number) {
        this.scroller.scrollTop = value;
        this.draw();
    }

    /**
     * Die Scrollposition, festgemacht an der obersten sichtbaren Zeile.
     *
     * Ein Pixelwert allein trägt nicht über einen Neuaufbau hinweg: Die Höhen
     * sind dann wieder Schätzungen, und derselbe Abstand zeigt auf eine andere
     * Zeile. Der Datensatz und der Versatz innerhalb seiner Zeile bleiben
     * dagegen gültig.
     */
    get anchor(): { key: number; delta: number } {
        const index = this.indexAt(this.scroller.scrollTop);
        return { key: this.rows[index]?.key ?? 0, delta: this.scroller.scrollTop - this.offsets[index] };
    }

    /** Stellt eine mit `anchor` gemerkte Position wieder her. */
    restoreAnchor(key: number, delta: number): boolean {
        const index = this.rows.findIndex((r) => r.key === key);
        if (index < 0) return false;

        this.scroller.scrollTop = this.offsets[index] + delta;
        this.draw();
        // Das Zeichnen misst die eingehängten Zeilen nach und verschiebt damit
        // alle Abstände darüber. Ohne diese zweite Runde stünde die Liste um
        // genau diese Differenz daneben.
        this.scroller.scrollTop = this.offsets[index] + delta;
        this.draw();
        return true;
    }

    private recalc(): void {
        this.offsets = new Array(this.rows.length + 1);
        let sum = 0;
        for (let i = 0; i < this.rows.length; i++) {
            this.offsets[i] = sum;
            sum += this.heights[i];
        }
        this.offsets[this.rows.length] = sum;
    }

    /** Erste Zeile, die bei diesem Abstand noch sichtbar ist (binäre Suche). */
    private indexAt(y: number): number {
        let lo = 0;
        let hi = this.rows.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (this.offsets[mid + 1] <= y) lo = mid + 1;
            else hi = mid;
        }
        return lo;
    }

    private schedule(): void {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.draw();
        });
    }

    /**
     * Abstandszeile: trägt keinen Inhalt, nur Höhe. Sie muss trotzdem alle
     * Spalten überspannen, sonst zieht sie das Spaltenraster schief.
     */
    private makeSpacer(): HTMLTableRowElement {
        const tr = document.createElement("tr");
        tr.className = "hi-vspacer";
        const td = document.createElement("td");
        td.colSpan = this.columns;
        tr.append(td);
        return tr;
    }

    /**
     * Baut die Elemente einer Zeile aus ihrem HTML.
     *
     * Über ein <template>, weil sich `<tr>` nirgendwo sonst freistehend
     * parsen lässt — außerhalb einer Tabelle verwirft der Parser die Zeile
     * samt Zellen.
     */
    private rowElements(index: number): HTMLElement[] {
        const template = document.createElement("template");
        template.innerHTML = this.rows[index].html.trim();
        return Array.from(template.content.children) as HTMLElement[];
    }

    private draw(): void {
        if (this.rows.length === 0) return;
        const sicht = this.scroller.clientHeight || 600;
        const oben = this.scroller.scrollTop;
        // Ein Sichtfenster Vorlauf in beide Richtungen: Beim Scrollen ist die
        // nächste Zeile dann schon da, statt erst gebaut zu werden.
        const von = this.indexAt(Math.max(0, oben - sicht));
        const bis = this.indexAt(oben + 2 * sicht);

        if (von === this.von && bis === this.bis) return;

        // Was aus dem Fenster gelaufen ist, fliegt raus …
        const weg: HTMLElement[] = [];
        for (const [index, elemente] of this.rendered) {
            if (index >= von && index <= bis) continue;
            for (const el of elemente) el.remove();
            weg.push(...elemente);
            this.rendered.delete(index);
        }
        if (weg.length > 0) this.onRowsRemoved?.(weg);

        // … und nur das Fehlende kommt dazu. Von hinten nach vorn, damit die
        // schon eingehängte Nachfolgezeile als Einfügemarke dienen kann.
        const neu: HTMLElement[] = [];
        for (let i = bis; i >= von; i--) {
            if (this.rendered.has(i)) continue;
            const elemente = this.rowElements(i);
            const marke = this.rendered.get(i + 1)?.[0] ?? this.bottomSpacer;
            for (const el of elemente) this.tbody.insertBefore(el, marke);
            this.rendered.set(i, elemente);
            neu.push(...elemente);
        }

        this.von = von;
        this.bis = bis;
        this.measure();
        this.updateSpacers();
        if (neu.length > 0) this.afterDraw(neu);
    }

    /**
     * Misst die eingehängten Zeilen nach.
     *
     * Ändert sich dadurch etwas, werden nur die Abstandszeilen korrigiert —
     * das Fenster selbst bleibt stehen. Ein erneutes Zeichnen mitten im
     * Messen würde sonst unter dem Finger springen; die nächste Scrollbewegung
     * richtet es ohnehin gerade.
     */
    private measure(): void {
        let geaendert = false;
        for (const [index, elemente] of this.rendered) {
            let hoehe = 0;
            for (const el of elemente) hoehe += el.offsetHeight;
            if (hoehe > 0 && this.heights[index] !== hoehe) {
                this.heights[index] = hoehe;
                geaendert = true;
            }
        }
        if (geaendert) this.recalc();
    }

    private updateSpacers(): void {
        const vorher = Math.max(0, this.offsets[this.von]);
        const nachher = Math.max(0, this.offsets[this.rows.length] - this.offsets[this.bis + 1]);
        this.topSpacer.style.height = `${Math.round(vorher)}px`;
        this.bottomSpacer.style.height = `${Math.round(nachher)}px`;
    }
}

interface DialogOptions {
    title: string;
    message: string;
    /**
     * Aufzählung unter der Meldung. Für Aktionen, die mehr löschen als das
     * angeklickte Objekt — dort muss einzeln aufgeführt sein, was mit
     * verschwindet, statt es in einen Fließtext zu packen.
     */
    details?: string[];
    confirmLabel?: string;
    /** null unterdrückt den Abbrechen-Button (reiner Hinweis-Dialog). */
    cancelLabel?: string | null;
    danger?: boolean;
}

/**
 * Wartezustand an Bedienelementen
 * ------------------------------------------------------------------
 * Serveranfragen dauern spürbar. Ohne Rückmeldung wirkt der Knopf, als hätte
 * er nicht reagiert, und wird ein zweites Mal gedrückt. Der Zustand sitzt
 * deshalb direkt am auslösenden Element.
 *
 * Der Spinner erscheint erst nach einer kurzen Verzögerung. Rein
 * clientseitige Aktionen — navigieren, sortieren, aufklappen — laufen durch
 * dieselbe Stelle und sind sofort fertig; ohne die Verzögerung würde bei
 * jedem Klick kurz etwas aufblitzen.
 */
const BUSY_DELAY_MS = 120;

interface BusyState {
    element: HTMLElement;
    timer: number;
}

/**
 * Ersatz für `field-sizing: content` in Browsern, die es noch nicht kennen:
 * Das Textfeld wird so hoch wie sein Inhalt.
 *
 * Wo die CSS-Eigenschaft greift, macht der Browser das selbst und besser —
 * dann hält sich diese Funktion heraus, statt mit einer gesetzten Höhe
 * dagegenzuhalten. Ohne den Ersatz bliebe der Text angeschnitten, denn das
 * Feld scrollt nicht (`overflow: hidden`).
 */
function autoGrow(field: HTMLTextAreaElement): void {
    if (CSS.supports("field-sizing", "content")) return;
    field.style.height = "auto";
    // scrollHeight zählt den Rahmen nicht mit, die gesetzte Höhe schon
    // (border-box) — ohne diesen Zuschlag fehlte unten genau die Rahmenstärke
    // und die letzte Zeile wäre angeschnitten.
    const rahmen = field.offsetHeight - field.clientHeight;
    field.style.height = field.scrollHeight + rahmen + "px";
}

/** Aktuell wartendes Element — modulweit, damit showDialog() es aussetzen kann. */
let busy: BusyState | null = null;

function paintBusy(element: HTMLElement, on: boolean): void {
    element.toggleAttribute("data-busy", on);
    if (on) {
        element.setAttribute("aria-busy", "true");
    } else {
        element.removeAttribute("aria-busy");
    }
    // Sperrt nebenbei den Doppelklick auf denselben Knopf.
    if (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) {
        element.disabled = on;
    }
}

function startBusy(element: HTMLElement): BusyState {
    return { element, timer: window.setTimeout(() => paintBusy(element, true), BUSY_DELAY_MS) };
}

function stopBusy(state: BusyState | null): void {
    if (!state) return;
    window.clearTimeout(state.timer);
    // Nach einem render() hängt das Element nicht mehr im Dokument — dann
    // gibt es nichts zurückzunehmen.
    if (state.element.isConnected) paintBusy(state.element, false);
}

/** Führt eine Aktion aus und zeigt sie so lange am auslösenden Element an. */
async function withBusy<T>(trigger: HTMLElement | null, run: () => Promise<T>): Promise<T> {
    if (!trigger) return run();
    const previous = busy;
    busy = startBusy(trigger);
    try {
        return await run();
    } finally {
        stopBusy(busy);
        busy = previous;
    }
}

/**
 * Eigene Dialog-Box über das jsidea.core-Modal statt der nativen
 * confirm()/alert() — damit lässt sich ein Abbrechen-Button anbieten und
 * die Aktion konkret in eigenen Worten beschreiben statt der knappen
 * Browser-Standarddialoge.
 */
function showDialog(options: DialogOptions): Promise<boolean> {
    // Solange der Dialog offen ist, wartet die App auf den Nutzer und nicht
    // auf den Server. Ein Spinner am auslösenden Knopf würde hier Arbeit
    // vortäuschen, die gar nicht läuft.
    const paused = busy;
    stopBusy(paused);
    busy = null;
    // Nur einmal fortsetzen: Beim Bestätigen läuft resume() direkt und gleich
    // darauf noch einmal über onClose. Zwei Aufrufe legen zwei Timer an, das
    // abschließende stopBusy in withBusy kennt aber nur den zweiten — der erste
    // feuert danach und lässt den Knopf für immer im Wartezustand stehen.
    let resumed = false;
    const resume = () => {
        if (resumed) return;
        resumed = true;
        if (paused) busy = startBusy(paused.element);
    };

    return new Promise((resolve) => {
        const cancelLabel = options.cancelLabel === undefined ? "Abbrechen" : options.cancelLabel;
        jsidea.modal.show({
            title: options.title,
            className: "hi-dialog",
            width: 440,
            // Ohne Startfokus bliebe der Fokus auf dem Knopf HINTER dem
            // Overlay stehen — also auf dem Löschknopf, der den Dialog eben
            // ausgelöst hat. Ein Enter würde dann den Auslöser erneut treffen
            // statt den Dialog zu bedienen. "first" ist das Schließkreuz der
            // Kopfzeile: Enter und Leertaste brechen damit ab, und die
            // gefährliche Schaltfläche ist nur zu erreichen, wenn man
            // ausdrücklich dorthin tabbt oder klickt.
            focus: "first",
            onClose: () => {
                resume();
                resolve(false);
            },
            content: (info) => {
                const el = document.createElement("div");
                el.innerHTML = `
                    <p class="hi-dialog__message">${Views.esc(options.message)}</p>
                    ${options.details?.length ? `
                    <ul class="hi-dialog__list">
                        ${options.details.map((d) => `<li>${Views.esc(d)}</li>`).join("")}
                    </ul>` : ""}
                    <div class="hi-dialog__actions">
                        ${cancelLabel ? `<button type="button" class="hi-btn hi-btn--ghost" data-modal-action="close">${Views.esc(cancelLabel)}</button>` : ""}
                        <button type="button" class="hi-btn${options.danger ? " hi-btn--danger" : ""}" data-dialog-confirm>${Views.esc(options.confirmLabel || "OK")}</button>
                    </div>`;
                el.querySelector<HTMLButtonElement>("[data-dialog-confirm]")?.addEventListener("click", () => {
                    // Vor dem Schließen: ab hier läuft wieder echte Arbeit.
                    resume();
                    resolve(true);
                    info.close();
                });
                return el;
            },
        });
    });
}

function confirmDialog(
    title: string,
    message: string,
    options: { confirmLabel?: string; danger?: boolean; details?: string[] } = {}
): Promise<boolean> {
    return showDialog({
        title,
        message,
        details: options.details,
        confirmLabel: options.confirmLabel ?? "Löschen",
        danger: options.danger ?? true,
    });
}

async function alertDialog(title: string, message: string): Promise<void> {
    await showDialog({ title, message, cancelLabel: null, confirmLabel: "OK" });
}

/**
 * Kopiert Text in die Zwischenablage. Die asynchrone Clipboard-API steht nur
 * in sicheren Kontexten (HTTPS) zur Verfügung — die lokale .test-Site läuft
 * über HTTP, deshalb der execCommand-Rückweg über ein unsichtbares Textfeld.
 */
async function copyText(text: string): Promise<boolean> {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Weiter zum Rückweg über execCommand.
        }
    }
    const feld = document.createElement("textarea");
    feld.value = text;
    feld.setAttribute("readonly", "");
    feld.style.position = "fixed";
    feld.style.opacity = "0";
    document.body.appendChild(feld);
    feld.select();
    let ok = false;
    try {
        ok = document.execCommand("copy");
    } catch {
        ok = false;
    }
    feld.remove();
    return ok;
}

/**
 * Kurze Rückmeldung direkt am Kopier-Knopf — die Beschriftung wechselt für
 * einen Moment, ohne dass die Ansicht neu gerendert wird (ein Re-Render würde
 * den Moment sofort wieder überschreiben).
 */
function flashCopied(trigger: HTMLElement | null, ok: boolean): void {
    const knopf = trigger?.closest("button");
    if (!knopf) return;
    const original = knopf.innerHTML;
    knopf.innerHTML = ok ? "Kopiert ✓" : "Kopieren fehlgeschlagen";
    knopf.disabled = true;
    window.setTimeout(() => {
        knopf.innerHTML = original;
        knopf.disabled = false;
    }, 1400);
}

/**
 * Rückmeldung an einer kopierten Datensatz-Nummer. Anders als bei den
 * Kopier-Knöpfen der MCP-Ansicht bleibt die Beschriftung stehen — die Nummer
 * steht in Tabellenzellen, ein Textwechsel würde die Spalte springen lassen.
 * Die Bestätigung hängt deshalb als Marke am Knopf (siehe `hi-id--copied`).
 */
function flashCopiedId(knopf: HTMLElement, ok: boolean): void {
    const klasse = ok ? "hi-id--copied" : "hi-id--failed";
    knopf.classList.add(klasse);
    window.setTimeout(() => knopf.classList.remove(klasse), 1200);
}

function touchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
}

/** Mittelpunkt zwischen zwei Fingern — Zoom-Zentrum einer Pinch-Geste. */
function touchCenter(touches: TouchList): { x: number; y: number } {
    return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    };
}

export class App {
    root: HTMLElement;
    positions: Position[] = [];
    items: Item[] = [];
    rooms: Room[] = [];
    /** Hinweise aus dem Foto-Import — siehe Ansicht „Import-Hinweise". */
    notes: Note[] = [];
    userName: string;
    /**
     * Darf dieser Zugang den gesamten Bestand löschen? Serverseitig gesetzt und
     * nur auf der Entwicklungsmaschine wahr (siehe
     * InventoryApp::mayResetEverything). Steuert allein, ob die Gefahrenzone
     * unter „KI-Zugriff" überhaupt erscheint.
     */
    canReset = false;
    /** Läuft gerade ein Rundumschlag? Text für die Fortschrittsanzeige. */
    resetProgress: string | null = null;
    /**
     * Name dieser Instanz — siehe INSTANCE_LABEL_KEY. Leer, solange niemand
     * ihn vergeben hat; dann zeigt das Feld nur seinen Platzhaltertext.
     */
    instanceLabel: string = readStored(INSTANCE_LABEL_KEY);

    view: ViewName = "dashboard";
    selectedId: number | null = null;
    /**
     * Gegenstand, auf den die Positionsansicht springen soll (Sprungmarke aus
     * der Positionsliste, `?view=position&id=…&item=…`). Bleibt gesetzt,
     * solange die Position offen ist — die Zeile bleibt dadurch markiert und
     * der Link ist als Lesezeichen brauchbar.
     */
    focusItemId: number | null = null;
    /** Sorgt dafür, dass nur die erste Darstellung nach dem Sprung scrollt. */
    private pendingFocusScroll = false;

    listFilter = { q: "", raum: "" };
    /** Spaltensortierung der Positionsliste (statt manuellem Drag & Drop), im localStorage gemerkt. */
    listSort: { key: "nummer" | "title" | "raum" | "items" | "stueck" | "wert" | "geprueft"; dir: "asc" | "desc" } =
        readSortPreference("positions", ["nummer", "title", "raum", "items", "stueck", "wert", "geprueft"], { key: "nummer", dir: "asc" });
    /** Positionen, deren Gegenstände in der Positionsliste aufgeklappt sind. */
    expandedPositions = new Set<number>();
    /** Freitextfilter der Raumliste (Name oder Beschreibung). */
    roomFilter = { q: "" };
    /** Spaltensortierung der Raumliste, analog zur Positionsliste. */
    roomSort: { key: "id" | "title" | "positions" | "items" | "stueck" | "wert"; dir: "asc" | "desc" } =
        readSortPreference("rooms", ["id", "title", "positions", "items", "stueck", "wert"], { key: "title", dir: "asc" });
    /** Räume, deren Positionen in der Raumliste aufgeklappt sind. */
    expandedRooms = new Set<number>();
    /** Filter der Gegenstandsliste: Freitext, Raum, Quelle und ein Zustandsfilter. */
    itemFilter = { q: "", raum: "", quelle: "", state: "" };
    /**
     * Filter der Import-Hinweise: Wichtigkeit, Freitext, Erledigt-Status.
     *
     * Startet auf „offen" — erledigte Hinweise bleiben als Nachweis erhalten,
     * sind aber keine Aufgabe mehr und würden die Liste sonst zuwachsen lassen.
     */
    noteFilter: { level: string; q: string; state: string } = { level: "", q: "", state: "offen" };
    /** Hinweis, der gerade bearbeitet wird; -1 = neuer Hinweis. */
    editingNoteId: number | null = null;
    /**
     * Mehrfachauswahl in den Listen (Räume, Positionen, Gegenstände):
     * Strg/Cmd-Klick toggelt einzelne Zeilen, Shift-Klick markiert den Bereich
     * seit dem letzten Anker. Die Summenzeile der Liste zeigt Anzahl und
     * Gesamtwert der Auswahl. Bewusst ohne Checkboxen — die Zeile selbst ist
     * die Klickfläche, ein Klick ohne Modifikator öffnet wie bisher.
     */
    listSelection: { kind: Views.VirtualList | null; ids: Set<number>; anchor: number | null } =
        { kind: null, ids: new Set(), anchor: null };
    /** Spaltensortierung der Gegenstandsliste. */
    itemSort: {
        key: "id" | "title" | "anzahl" | "wert" | "gesamt" | "quelle" | "position" | "raum" | "geprueft";
        dir: "asc" | "desc";
    } = readSortPreference(
        "items",
        ["id", "title", "anzahl", "wert", "gesamt", "quelle", "position", "raum", "geprueft"],
        { key: "title", dir: "asc" }
    );
    /**
     * MCP-Zugänge der Ansicht „KI-Zugriff". null = noch nie geladen; die
     * Liste kommt erst beim ersten Betreten der Ansicht vom Server.
     */
    mcpCredentials: McpCredential[] | null = null;
    /** Zugänge, deren Passwort gerade im Klartext angezeigt wird. */
    mcpRevealed = new Set<string>();
    /* ---------------------------------------------------- Vergleich */
    /** Hinterlegter Zugang zur Referenz-Instanz; null = noch keiner. */
    compareReference: ReferenceConfig | null = readReference();
    /** Zuletzt geholter Datenstand der Referenz — bleibt für die Sitzung. */
    compareSnapshot: Snapshot | null = null;
    /** Ergebnis des letzten Vergleichs. */
    compareResult: CompareResult | null = null;
    /** Läuft gerade ein Abruf? Sperrt den Knopf und zeigt den Zustand. */
    compareLoading = false;
    /** Letzter Fehler im Klartext — die Ansicht zeigt ihn statt der Liste. */
    compareError = "";
    compareFilter = { kind: "", kategorie: "", q: "" };
    /** Steht das Formular für den Zugang offen? Ohne Zugang immer. */
    compareEditOpen = false;
    /** Zeigt auch Reihenfolge und Import-Protokolle. */
    compareShowMinor = readStored(COMPARE_MINOR_KEY) === "1";
    /**
     * Wie viele Unterschiede die Liste höchstens zeigt.
     *
     * Bei zwei weit auseinandergelaufenen Ständen können es tausende sein —
     * die alle auf einmal zu zeichnen, blockiert den Browser für Sekunden,
     * ohne dass jemand so viel auf einmal liest.
     */
    compareLimit = 200;
    /** Eingabe der globalen Suche im Kopf der Seitenleiste. */
    searchQuery = "";
    /**
     * Zählt bei jedem reload() hoch. Der Suchindex hängt daran: Er wird über
     * alle Räume, Positionen und Gegenstände aufgebaut und darf nicht bei
     * jedem Tastendruck neu entstehen — aber sofort veralten, wenn sich etwas
     * geändert hat.
     */
    dataStamp = 0;
    editingPosition = false;
    editingRoom = false;
    addingItem = false;
    editingItemId: number | null = null;
    roomFormOpen = false;
    newItemPhotos: GalleryImage[] = [];
    newPositionPhotos: GalleryImage[] = [];
    exportFormat: "csv" | "pdf" | "xlsx" = "pdf";
    exportScope: "alle" | "geprueft" = "alle";
    /**
     * Wie die Aufstellung nach Räumen gegliedert wird.
     * "page" ist die von der Versicherung erwartete Form: Raum im Seitenkopf,
     * je Raum ein eigener Satz Blätter.
     */
    exportRoomMode: "page" | "row" | "none" = "page";
    exportGroupItems = true;
    exportEmbedImages = false;
    /** Name für den Seitenkopf der Schadenaufstellung; bleibt zwischen Sitzungen erhalten. */
    exportOwner = readStored(EXPORT_OWNER_KEY);
    /** Ort für die Unterschriftszeile; ändert sich selten, wird daher gemerkt. */
    exportPlace = readStored(EXPORT_PLACE_KEY);
    /**
     * Datum für die Unterschriftszeile (ISO). Bewusst nicht gemerkt: Leer
     * bedeutet "heute", und genau das ist beim nächsten Export der richtige
     * Standard — ein gespeichertes Datum wäre irgendwann still veraltet.
     */
    exportDate = "";
    /** Was in der Euro-Spalte steht: Gesamtwert der Zeile oder Preis je Stück. */
    exportValueMode: "gesamt" | "stueck" = "gesamt";
    /**
     * Unterschriftszeile nur auf dem letzten Blatt (Standard).
     *
     * Eine Aufstellung über zwanzig Seiten auf jedem Blatt zu unterschreiben
     * ist eine Zumutung, und die Versicherung verlangt es in aller Regel nur
     * einmal. Wer es anders braucht, schaltet die Option ab.
     */
    exportSignatureLastPage = true;
    /**
     * Spalte "Alter" in der Aufstellung (Standard: an).
     *
     * Die Versicherung ermittelt aus dem Alter den Zeitwert und braucht die
     * Angabe deshalb im Regelfall. Abschalten lässt sie sich trotzdem: Für eine
     * Aufstellung, die nur die Wiederbeschaffungswerte belegen soll, ist die
     * Spalte überflüssig, und der frei werdende Platz kommt "Sonstiges" zugute.
     */
    exportShowAlter = true;
    /**
     * Erläuterung vor der Aufstellung (Standard: aus).
     *
     * Bewusst abgeschaltet: Die meisten Versicherungen erwarten die nackte
     * Liste, und ein Vorspann, der nicht angefordert war, verlängert nur die
     * Bearbeitung. Wer ihn braucht — etwa weil viele Beträge geschätzt sind —
     * schaltet ihn ein.
     */
    exportIntro = false;
    /**
     * Der Text dieser Erläuterung. Bearbeitbar und zwischen Sitzungen gemerkt,
     * damit eine einmal angepasste Fassung nicht bei jedem Export neu
     * geschrieben werden muss. Leer bedeutet: wieder die Vorbelegung.
     */
    exportIntroText = readStored(EXPORT_INTRO_KEY) || DEFAULT_EXPORT_INTRO;
    /** Ansicht des letzten render() — unter diesem Schlüssel wird ihre Scrollposition gesichert. */
    private lastRenderKey = "";
    /**
     * Zuletzt gesehene Scrollposition je Ansicht (`view:id`).
     *
     * Der Inhaltsbereich `#hi-content` scrollt selbst, nicht das Dokument —
     * die Scroll-Wiederherstellung des Browsers greift hier also nicht. Ohne
     * dieses Gedächtnis beginnt die Positionsliste nach jedem Ausflug in eine
     * Position wieder ganz oben, was bei 150 Positionen jedes Mal neues
     * Suchen bedeutet.
     */
    private scrollOffsets = new Map<string, number>();
    /** Session-Reihenfolge des Prüf-Assistenten (Item-IDs), persistiert bis alles geprüft ist. */
    reviewQueue: number[] = [];

    constructor(root: HTMLElement) {
        this.root = root;
        this.userName = jsidea.wp.config.hausinventar?.userName || "Nutzer";
        this.canReset = jsidea.wp.config.hausinventar?.canReset === true;
        this.applyDocumentTitle();
    }

    /**
     * Spiegelt den Instanznamen in den Browser-Tab-Titel — dort fällt der
     * Unterschied zwischen mehreren offenen Instanzen am ehesten auf, noch
     * bevor man in eine der Seiten hineinklickt.
     */
    private applyDocumentTitle(): void {
        const label = this.instanceLabel.trim();
        document.title = label ? `${label} – HausInventar` : "HausInventar";
    }

    async boot(): Promise<void> {
        this.renderBoot();
        // Nur der Erststart zeigt Fortschritt. Die späteren reload()-Aufrufe
        // nach dem Speichern laufen im Hintergrund und würden sonst eine
        // Ladeanzeige ansprechen, die längst durch die App ersetzt ist.
        setLoadProgress((p) => this.updateBoot(p));
        try {
            await this.reload();
        } finally {
            setLoadProgress(null);
        }
        // Erst nach dem Erststart: Den zeigt bereits die Ladeanzeige.
        this.mountSaveStatus();
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") void this.backgroundSync();
        });
        this.bindEvents();

        window.addEventListener("popstate", () => {
            const { view, id, item } = this.parseUrl();
            // Modals aus jsidea.core legen beim Öffnen einen History-Eintrag
            // an und gehen beim Schließen per history.back() zurück. Das löst
            // hier ein popstate aus, obwohl die Ansicht dieselbe geblieben
            // ist — ohne diese Prüfung baut die App ihr DOM grundlos neu auf.
            if (view === this.view && id === this.selectedId && item === this.focusItemId) return;
            this.applyState(view, id, item);
        });

        const { view, id, item } = this.parseUrl();
        this.applyState(view, id, item);
        this.replaceUrl();
    }

    /**
     * Statusanzeige „Speichert …" — sichtbar, solange etwas geschrieben oder
     * danach neu geladen wird, und kurz „Gespeichert", wenn alles durch ist.
     *
     * Sie hängt an document.body und nicht in der App: render() ersetzt das
     * gesamte App-DOM, die Anzeige muss aber gerade während dieser Neuaufbauten
     * stehen bleiben. Und sie liegt über allen Modals, weil auch aus dem
     * Ausschnitt-Editor heraus gespeichert wird.
     *
     * Zwischen dem Ende des Schreibens und dem Beginn des Neuladens liegt ein
     * Wimpernschlag ohne laufenden Vorgang. Das Ausblenden wartet deshalb kurz
     * — sonst flackerte die Anzeige mitten in einem Speichervorgang auf
     * „Gespeichert" und wieder zurück.
     */
    private mountSaveStatus(): void {
        const el = document.createElement("div");
        el.className = "hi-save-status";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        el.hidden = true;
        document.body.appendChild(el);

        let warGeschrieben = false;
        let timer = 0;

        const zeige = (state: WorkState) => {
            window.clearTimeout(timer);
            if (state.writes > 0 || state.reloads > 0) {
                // Nur Neuladen ohne vorheriges Schreiben (etwa der Vergleich)
                // ist kein Speichern und heißt deshalb anders.
                warGeschrieben ||= state.writes > 0;
                const text = warGeschrieben ? "Speichert …" : "Aktualisiert …";
                el.className = "hi-save-status hi-save-status--busy";
                el.innerHTML = `${Views.icon("spinner-third", "fa-spin")} ${text}`;
                el.hidden = false;
                return;
            }
            timer = window.setTimeout(() => {
                if (!warGeschrieben) {
                    el.hidden = true;
                    return;
                }
                warGeschrieben = false;
                el.className = "hi-save-status hi-save-status--done";
                el.innerHTML = `${Views.icon("check")} Gespeichert`;
                timer = window.setTimeout(() => (el.hidden = true), 1600);
            }, 250);
        };
        onWorkChange(zeige);

        // Wer die Seite verlässt, während noch geschrieben wird, verliert die
        // Änderung womöglich — der Browser fragt dann einmal nach.
        window.addEventListener("beforeunload", (e) => {
            if (workState().writes === 0) return;
            e.preventDefault();
            e.returnValue = "";
        });
    }

    async reload(): Promise<void> {
        const daten = await this.fetchAll();
        this.adopt(daten);
    }

    /** Der gesamte Bestand vom Server — ohne ihn schon zu übernehmen. */
    private fetchAll(): Promise<[Position[], Item[], Room[], Note[]]> {
        return track("reloads", () => Promise.all([
            Api.listPositions(),
            Api.listItems(),
            Api.listRooms(),
            Api.listNotes(),
        ]));
    }

    private adopt([positions, items, rooms, notes]: [Position[], Item[], Room[], Note[]]): void {
        this.positions = positions;
        this.items = items;
        this.rooms = rooms;
        this.notes = notes;
        this.dataStamp++;
        this.lastSync = Date.now();
    }

    /* ------------------------------------- Lokaler Stand nach dem Speichern */

    /**
     * Zählt jede lokale Übernahme eines gespeicherten Datensatzes.
     *
     * Der Hintergrund-Abgleich (backgroundSync) vergleicht ihn vor und nach
     * seinem Abruf: Hat sich zwischendurch etwas geändert, ist sein Abruf
     * älter als der lokale Stand und wird verworfen — sonst überschriebe er
     * die gerade gespeicherte Änderung mit dem Wert von vorher.
     */
    private localEdits = 0;
    /** Zeitpunkt des letzten vollständigen Abrufs. */
    private lastSync = 0;
    private syncRunning = false;

    /*
     * Nach einem Schreibzugriff übernimmt die App den gespeicherten Datensatz
     * direkt aus der Antwort, statt den ganzen Bestand neu zu laden. Das
     * Neuladen dauerte ~1,5–5 s, die Übernahme ist sofort da. Jede Übernahme
     * erhöht dataStamp, damit Suchindex und Duplikat-Verweise neu entstehen.
     */
    private static upsert<T extends { id: number }>(list: T[], records: T[]): T[] {
        const next = list.slice();
        for (const record of records) {
            const i = next.findIndex((r) => r.id === record.id);
            if (i < 0) next.push(record);
            else next[i] = record;
        }
        return next;
    }

    private changed(): void {
        this.dataStamp++;
        this.localEdits++;
    }

    private applyItems(...items: Item[]): void {
        this.items = App.upsert(this.items, items);
        this.changed();
    }

    private applyPosition(position: Position): void {
        this.positions = App.upsert(this.positions, [position]);
        this.changed();
    }

    private applyRoom(room: Room): void {
        this.rooms = App.upsert(this.rooms, [room]);
        this.changed();
    }

    private applyNote(note: Note): void {
        this.notes = App.upsert(this.notes, [note]);
        this.changed();
    }

    private dropItems(ids: number[]): void {
        const weg = new Set(ids);
        this.items = this.items.filter((i) => !weg.has(i.id));
        this.changed();
    }

    /** Position samt ihrer Gegenstände — genau das, was deletePositionCascade löscht. */
    private dropPosition(id: number): void {
        this.positions = this.positions.filter((p) => p.id !== id);
        this.items = this.items.filter((i) => i.position !== id);
        this.changed();
    }

    private dropRoom(id: number): void {
        this.rooms = this.rooms.filter((r) => r.id !== id);
        this.changed();
    }

    private dropNote(id: number): void {
        this.notes = this.notes.filter((n) => n.id !== id);
        this.changed();
    }

    /**
     * Holt Änderungen von außen nach, ohne die Arbeit zu stören.
     *
     * Bisher kam beim Speichern nebenbei auch an, was ein MCP-Import oder ein
     * anderer Tab geändert hatte. Seit nicht mehr nach jedem Speichern neu
     * geladen wird, übernimmt das dieser Abgleich — beim Zurückkehren in den
     * Tab, denn genau dann hat sich typischerweise woanders etwas getan.
     *
     * Verworfen wird der Abruf, wenn währenddessen hier gespeichert wurde
     * (siehe localEdits). Neu gezeichnet wird nur bei echten Änderungen und
     * nie mitten in einer Eingabe (renderWhenIdle).
     */
    private async backgroundSync(): Promise<void> {
        if (this.syncRunning || Date.now() - this.lastSync < 15000) return;
        if (workState().writes > 0) return;
        this.syncRunning = true;
        try {
            const vorher = this.localEdits;
            const daten = await this.fetchAll();
            if (this.localEdits !== vorher || workState().writes > 0) return;
            const alt = JSON.stringify([this.positions, this.items, this.rooms, this.notes]);
            const neu = JSON.stringify(daten);
            this.lastSync = Date.now();
            if (alt === neu) return;
            this.adopt(daten);
            this.renderWhenIdle();
        } catch {
            // Nächster Versuch beim nächsten Zurückkehren — ein gescheiterter
            // Hintergrundabruf ist keine Meldung wert.
        } finally {
            this.syncRunning = false;
        }
    }

    /** render(), aber nicht, während jemand in ein Feld der App tippt. */
    private renderWhenIdle(): void {
        const aktiv = document.activeElement;
        const tippt = (aktiv instanceof HTMLInputElement || aktiv instanceof HTMLTextAreaElement || aktiv instanceof HTMLSelectElement)
            && this.root.contains(aktiv);
        if (!tippt) {
            this.render();
            return;
        }
        aktiv.addEventListener("blur", () => window.setTimeout(() => this.render(), 0), { once: true });
    }

    /** Zuletzt gemeldeter Stand je Datenstrom, Grundlage der Boot-Anzeige. */
    private bootProgress = new Map<LoadProgress["key"], LoadProgress>();

    /**
     * Beobachter für die verzögert geladenen Ausschnitte der
     * Gegenstandsliste. Gemerkt, um ihn beim nächsten render() abzuschalten —
     * seine Elemente sind dann längst ersetzt.
     */
    private lazyCropObserver: IntersectionObserver | null = null;

    /** Virtualisierte Tabellen der aktuellen Ansicht. */
    private virtualTables: VirtualTable[] = [];

    /**
     * Ob eine Liste nur ihren sichtbaren Ausschnitt rendert. Die Einstellung
     * überlebt die Sitzung, weil sie eine Vorliebe ist und keine Eigenschaft
     * der Daten.
     */
    virtualLists: Record<Views.VirtualList, boolean> = {
        items: readVirtualPreference("items"),
        positions: readVirtualPreference("positions"),
        rooms: readVirtualPreference("rooms"),
    };

    /**
     * Scrollposition der Listentabellen je Ansicht. Gescrollt wird dort die
     * Tabelle selbst und nicht der Inhaltsbereich — das Merken in
     * scrollOffsets greift für sie also nicht.
     *
     * Gemerkt wird beides: der Pixelwert für vollständig gerenderte Tabellen
     * und zusätzlich die oberste sichtbare Zeile für virtualisierte, deren
     * Pixelwerte einen Neuaufbau nicht überstehen.
     */
    private tableScrollOffsets = new Map<string, { pixel: number; key: number; delta: number }>();

    /** Setzt das nächste render() die Liste an den Anfang? Siehe resetListScroll. */
    private listScrollReset = false;

    /**
     * Ladeanzeige des Erststarts: mittig, mit rotierendem Symbol und einem
     * Balken, der den tatsächlichen Ladestand zeigt.
     *
     * Der Bestand ist auf knapp 2.000 Gegenstände gewachsen; das Laden dauert
     * spürbar mehrere Sekunden. Ein reines "Lädt …" lässt in dieser Zeit offen,
     * ob überhaupt etwas passiert.
     */
    private renderBoot(): void {
        this.root.innerHTML = `
            <div class="hi-boot" role="status" aria-live="polite">
                <div class="hi-boot__box">
                    <i class="fal fa-arrows-rotate fa-spin hi-boot__spinner" aria-hidden="true"></i>
                    <p class="hi-boot__title">Hausinventar wird geladen</p>
                    <div class="hi-boot__bar hi-boot__bar--idle">
                        <div class="hi-boot__fill" style="width:0%"></div>
                    </div>
                    <p class="hi-boot__detail">Verbindung zum Server …</p>
                </div>
            </div>`;
    }

    private updateBoot(p: LoadProgress): void {
        this.bootProgress.set(p.key, p);

        const bar = this.root.querySelector<HTMLElement>(".hi-boot__bar");
        const fill = this.root.querySelector<HTMLElement>(".hi-boot__fill");
        const detail = this.root.querySelector<HTMLElement>(".hi-boot__detail");
        if (!bar || !fill || !detail) return;

        const streams = Array.from(this.bootProgress.values());

        /**
         * Anteil eines Stroms am eigenen Pensum.
         *
         * Die Datensätze machen den Löwenanteil der Wartezeit aus, das
         * anschließende Auflösen der Bild-IDs ist kürzer, aber nicht
         * vernachlässigbar — daher 85 % zu 15 %. Für die Medienphase gibt es
         * keinen Zwischenstand, sie springt von 0,85 auf 1.
         */
        const fraction = (s: LoadProgress): number => {
            if (s.phase === "done") return 1;
            if (s.phase === "media") return 0.85;
            return s.total > 0 ? 0.85 * (s.done / s.total) : 0;
        };

        // Gewichtet nach Datensatzzahl: 1.900 Gegenstände dürfen den Balken
        // nicht so schnell bewegen wie 15 Räume.
        const totalWeight = streams.reduce((sum, s) => sum + Math.max(s.total, 1), 0);
        const doneWeight = streams.reduce((sum, s) => sum + fraction(s) * Math.max(s.total, 1), 0);

        // Erst wenn alle drei Ströme ihre Gesamtzahl gemeldet haben, ist der
        // Prozentwert belastbar. Vorher bliebe er künstlich niedrig und würde
        // beim Eintreffen der letzten Gesamtzahl zurückspringen.
        const known = this.bootProgress.size === 3 && streams.every((s) => s.total > 0);
        bar.classList.toggle("hi-boot__bar--idle", !known);
        if (known) {
            fill.style.width = `${Math.round((doneWeight / totalWeight) * 100)}%`;
        }

        const order: LoadProgress["key"][] = ["positions", "items", "rooms"];
        detail.textContent = order
            .map((key) => this.bootProgress.get(key))
            .filter((s): s is LoadProgress => Boolean(s))
            .map((s) => {
                if (s.phase === "media") return `${s.label}: Bilder werden zugeordnet …`;
                const count = s.total > 0 ? `${s.done.toLocaleString("de-DE")} / ${s.total.toLocaleString("de-DE")}` : s.done.toLocaleString("de-DE");
                return `${s.label} ${count}`;
            })
            .join(" · ");
    }

    roomName(id: number): string {
        return this.rooms.find((r) => r.id === id)?.title || "";
    }

    setView(view: ViewName, id: number | null = null, itemId: number | null = null): void {
        this.applyState(view, id, itemId);
        this.pushUrl();
    }

    /**
     * Öffnet die Position eines Gegenstands und springt zu dessen Zeile.
     * Ziel der Gegenstands-Links in der Positionsliste.
     */
    openItem(itemId: number): void {
        const item = this.items.find((i) => i.id === itemId);
        if (!item) return;
        this.setView("position", item.position, itemId);
    }

    /** Aktualisiert den Zustand und rendert neu, ohne die Browser-Historie anzufassen. */
    private applyState(view: ViewName, id: number | null, itemId: number | null = null): void {
        this.view = view;
        this.selectedId = id;
        // Eine Sprungmarke gilt nur für die Positionsansicht; sonst bliebe sie
        // in der URL stehen und markierte beim Zurückblättern das Falsche.
        const focus = view === "position" ? itemId : null;
        this.pendingFocusScroll = focus != null && focus !== this.focusItemId;
        this.focusItemId = focus;
        this.editingPosition = false;
        this.editingRoom = false;
        this.addingItem = false;
        this.editingItemId = null;
        // Muss hier (statt nur im "nav"-Klick-Handler) passieren, da diese
        // Methode auch beim direkten Laden einer URL wie "?view=review"
        // durchlaufen wird (boot() -> parseUrl() -> applyState()), ohne
        // dass je ein Klick auf den Nav-Eintrag stattfand.
        if (view === "review" && this.reviewQueue.length === 0) {
            this.reviewQueue = this.items.filter((i) => !i.geprueft).map((i) => i.id);
        }
        // Ebenfalls URL-fest (siehe oben): Die MCP-Zugänge werden erst beim
        // ersten Betreten der Ansicht geladen — sie gehören nicht zum
        // Datenbestand des Inventars und würden den Start sonst verlängern.
        if (view === "mcp" && this.mcpCredentials === null) {
            void this.loadMcpCredentials();
        }
        this.render();
    }

    /** Lädt die MCP-Zugänge nach; rendert nur, wenn die Ansicht noch offen ist. */
    private async loadMcpCredentials(): Promise<void> {
        try {
            this.mcpCredentials = await Api.listMcpCredentials();
        } catch (error) {
            this.mcpCredentials = [];
            await alertDialog("Verbindungen laden fehlgeschlagen", error instanceof Error ? error.message : String(error));
        }
        if (this.view === "mcp") {
            this.render();
        }
    }

    /* ------------------------------------------------------- Vergleich */

    /**
     * Holt den Datenstand der Referenz und stellt ihn dem eigenen gegenüber.
     *
     * Der eigene Stand kommt aus dem Speicher der App — er ist beim Laden
     * ohnehin vollständig da. Geholt wird also nur die Gegenseite, und zwar
     * bei jedem Lauf frisch: Ein Vergleich, der einen alten Abzug zeigt, wäre
     * schlimmer als gar keiner.
     */
    private async runCompare(): Promise<void> {
        if (!this.compareReference || this.compareLoading) return;

        this.compareLoading = true;
        this.compareError = "";
        this.render();

        try {
            const referenz = await fetchReferenceSnapshot(this.compareReference);
            if (Number(referenz.schema) !== SNAPSHOT_SCHEMA) {
                throw new Error(`Die Referenz liefert Formatversion ${referenz.schema}, diese Instanz erwartet ${SNAPSHOT_SCHEMA}. Bringen Sie beide auf dieselbe Theme-Fassung.`);
            }
            this.compareSnapshot = referenz;
            this.compareResult = diffSnapshots(this.localSnapshot(), referenz);
            this.compareLimit = 200;
        } catch (error) {
            this.compareSnapshot = null;
            this.compareResult = null;
            this.compareError = error instanceof Error ? error.message : String(error);
        } finally {
            this.compareLoading = false;
            this.render();
        }
    }

    /** Der eigene Datenstand in der Form, die auch die Referenz liefert. */
    private localSnapshot(): Snapshot {
        return buildLocalSnapshot(
            { rooms: this.rooms, positions: this.positions, items: this.items, notes: this.notes },
            {
                name: this.instanceLabel || document.title.replace(/ – HausInventar$/, ""),
                url: location.origin,
                version: "",
                erzeugt: new Date().toISOString(),
            },
        );
    }

    /** Übernimmt den Zugang aus dem Formular und vergleicht sofort. */
    private async saveReference(form: HTMLFormElement): Promise<void> {
        const data = new FormData(form);
        const wert = (name: string) => String(data.get(name) ?? "").trim();
        const referenz: ReferenceConfig = {
            url: wert("url").replace(/\/+$/, ""),
            user: wert("user"),
            // Leer gelassen heißt "unverändert" — so lässt sich die Adresse
            // ändern, ohne das Passwort erneut heraussuchen zu müssen.
            secret: wert("secret") || this.compareReference?.secret || "",
        };
        if (!referenz.url || !referenz.user || !referenz.secret) {
            await alertDialog("Angaben unvollständig", "Adresse, Benutzername und Anwendungspasswort werden alle drei gebraucht.");
            return;
        }

        this.compareReference = referenz;
        this.compareEditOpen = false;
        writeStored(COMPARE_REFERENCE_KEY, JSON.stringify(referenz));
        await this.runCompare();
    }

    /** Vergisst Zugang und Ergebnis — der Knopf neben dem Hinweis zum Passwort. */
    private async forgetReference(): Promise<void> {
        const weiter = await confirmDialog(
            "Zugang vergessen?",
            "Adresse, Benutzername und Anwendungspasswort der Referenz werden aus diesem Browser gelöscht. Die Daten der Referenz selbst bleiben unberührt.",
            { confirmLabel: "Vergessen" },
        );
        if (!weiter) return;
        this.compareReference = null;
        this.compareSnapshot = null;
        this.compareResult = null;
        this.compareError = "";
        writeStored(COMPARE_REFERENCE_KEY, "");
        this.render();
    }

    private parseUrl(): { view: ViewName; id: number | null; item: number | null } {
        const params = new URL(window.location.href).searchParams;
        const rawView = params.get("view") || "dashboard";
        const view = (VIEW_NAMES as string[]).includes(rawView) ? (rawView as ViewName) : "dashboard";
        const rawId = params.get("id");
        const rawItem = params.get("item");
        // Der Suchbegriff gehört zur URL, damit ein Ergebnis teilbar und über
        // den Zurück-Knopf wieder erreichbar ist.
        if (view === "search") {
            this.searchQuery = params.get("q") || "";
        }
        return { view, id: rawId ? Number(rawId) : null, item: rawItem ? Number(rawItem) : null };
    }

    private buildUrl(): string {
        const url = new URL(window.location.href);
        if (this.view === "dashboard") {
            url.searchParams.delete("view");
            url.searchParams.delete("id");
        } else {
            url.searchParams.set("view", this.view);
            if (this.selectedId != null) {
                url.searchParams.set("id", String(this.selectedId));
            } else {
                url.searchParams.delete("id");
            }
        }
        if (this.view === "search" && this.searchQuery.trim()) {
            url.searchParams.set("q", this.searchQuery.trim());
        } else {
            url.searchParams.delete("q");
        }
        // Die Sprungmarke auf einen Gegenstand gehört zur Positionsansicht.
        if (this.focusItemId != null && this.view === "position") {
            url.searchParams.set("item", String(this.focusItemId));
        } else {
            url.searchParams.delete("item");
        }
        return url.toString();
    }

    private historyState(): { view: ViewName; id: number | null; item: number | null } {
        return { view: this.view, id: this.selectedId, item: this.focusItemId };
    }

    private pushUrl(): void {
        window.history.pushState(this.historyState(), "", this.buildUrl());
    }

    private replaceUrl(): void {
        window.history.replaceState(this.historyState(), "", this.buildUrl());
    }

    /** Schlüssel, unter dem die Scrollposition einer Ansicht gemerkt wird. */
    private renderKey(): string {
        return `${this.view}:${this.selectedId ?? ""}`;
    }

    render(): void {
        // Jedes render() ersetzt das komplette DOM und damit auch den
        // Scrollcontainer — ohne Sicherung stünde der Inhaltsbereich danach
        // wieder ganz oben. Das betrifft jede Kleinigkeit: Anzahl ändern,
        // Gegenstände aufklappen, einen Dialog schließen.
        //
        // Gesichert wird unter dem Schlüssel der Ansicht, die gerade noch im
        // DOM steht (`lastRenderKey`) — beim Ansichtswechsel ist das also die
        // Ansicht, die verlassen wird. Dadurch landet man beim Zurückgehen
        // wieder an derselben Stelle der Liste und nicht an deren Anfang.
        const key = this.renderKey();
        const leaving = this.root.querySelector<HTMLElement>("#hi-content");
        if (leaving && this.lastRenderKey) {
            this.scrollOffsets.set(this.lastRenderKey, leaving.scrollTop);
        }
        // Listenansichten scrollen in der Tabelle, nicht im Inhaltsbereich —
        // ihre Position muss deshalb getrennt gemerkt werden. Gelesen wird sie
        // vom Scrollcontainer selbst und nicht von der VirtualTable: Ohne
        // Virtualisierung gibt es keine.
        const listenScroller = this.root.querySelector<HTMLElement>("[data-table-scroll]");
        if (this.listScrollReset) {
            this.tableScrollOffsets.delete(this.lastRenderKey);
            this.listScrollReset = false;
        } else if (listenScroller && this.lastRenderKey) {
            const anker = this.virtualTables[0]?.anchor;
            this.tableScrollOffsets.set(this.lastRenderKey, {
                pixel: listenScroller.scrollTop,
                key: anker?.key ?? 0,
                delta: anker?.delta ?? 0,
            });
        }

        this.root.innerHTML = Views.renderShell(this, Views.renderView(this));
        this.lastRenderKey = key;

        // Eine gezielte Sprungmarke sticht die gemerkte Position: Wer aus der
        // Liste heraus einen bestimmten Gegenstand anklickt, will zu ihm und
        // nicht dorthin, wo er die Position zuletzt verlassen hat.
        if (!this.pendingFocusScroll) {
            this.restoreScroll(this.scrollOffsets.get(key) ?? 0);
        }

        this.scrollToFocusedItem();
        this.fitNoteFields();
        void this.hydrateCropThumbs();
        this.observeLazyCrops();
        this.mountVirtualTables(this.root, this.tableScrollOffsets.get(key));
    }

    /**
     * Verbindet die leeren Tabellenrümpfe mit ihren Zeilen und stellt die
     * Scrollposition der Liste wieder her.
     *
     * Die Ansichten liefern nur die Hülle (siehe tableShell in views.ts); die
     * Zeilen entstehen hier und werden von VirtualTable ausschnittweise
     * eingesetzt. Ohne das stünde nach jedem Rendern eine leere Tabelle da.
     *
     * Die Scrollposition wird erst **nach** dem Aufbau gesetzt: Solange die
     * Tabelle leer ist, hat sie keine Höhe, und der Browser kappt jeden Wert
     * auf 0 — die Liste stünde nach dem Zurückgehen wieder ganz oben.
     */
    private mountVirtualTables(scope: ParentNode, position?: { pixel: number; key: number; delta: number }): void {
        for (const table of this.virtualTables) table.destroy();
        this.virtualTables = [];

        const lieferant: Record<Views.VirtualList, () => Views.VirtualRow[]> = {
            positions: () => Views.positionsListRows(this),
            rooms: () => Views.roomsListRows(this),
            items: () => Views.itemsListRows(this),
        };

        for (const scroller of Array.from(scope.querySelectorAll<HTMLElement>("[data-table-scroll]"))) {
            const tbody = scroller.querySelector<HTMLElement>("[data-virtual]");
            const art = tbody?.getAttribute("data-virtual") as Views.VirtualList | null;

            // Ohne Virtualisierung stehen die Zeilen schon in der Hülle; dort
            // ist der Pixelwert exakt, weil sich an den Höhen nichts ändert.
            if (!tbody || !art || !lieferant[art]) {
                scroller.scrollTop = position?.pixel ?? 0;
                continue;
            }

            const table = new VirtualTable(
                scroller,
                tbody,
                lieferant[art](),
                Number(tbody.getAttribute("data-columns")) || 1,
                // Nur die neu eingehängten Zeilen: Die übrigen tragen ihren
                // Ausschnitt bereits und dürfen nicht angefasst werden.
                (neu) => this.watchLazyCrops(neu)
            );
            table.onRowsRemoved = (weg) => this.unwatchLazyCrops(weg);
            // Zuerst über die gemerkte Zeile; nur wenn es die nicht mehr gibt
            // (Filter geändert, Datensatz gelöscht), bleibt der Pixelwert.
            if (!position || position.key === 0 || !table.restoreAnchor(position.key, position.delta)) {
                table.scrollTop = position?.pixel ?? 0;
            }
            this.virtualTables.push(table);
        }
    }

    /**
     * Setzt die Scrollposition des Inhaltsbereichs.
     *
     * Zweimal: Direkt nach dem Einsetzen des DOM steht die endgültige Höhe
     * noch nicht fest (Ausschnitt-Vorschauen werden erst danach gefüllt,
     * Bilder laden nach). Ein zu großer Wert würde dann auf die vorläufige
     * Höhe gekappt und die Liste stünde doch wieder zu weit oben. Der zweite
     * Versuch im nächsten Frame korrigiert das, sobald das Layout steht.
     */
    private restoreScroll(offset: number): void {
        if (offset <= 0) return;
        const content = this.root.querySelector<HTMLElement>("#hi-content");
        if (!content) return;
        content.scrollTop = offset;
        window.requestAnimationFrame(() => {
            // Zwischenzeitliches Neu-Rendern oder ein Scrollen von Hand darf
            // nicht überschrieben werden.
            if (content.isConnected && content.scrollTop < offset) {
                content.scrollTop = offset;
            }
        });
    }

    /**
     * Springt zu dem Gegenstand, der über die Positionsliste angesteuert
     * wurde. Nur einmal je Sprung: Jedes weitere render() derselben Position
     * (Anzahl ändern, Prüfstatus umschalten) soll die Scrollposition des
     * Nutzers nicht wieder wegziehen.
     */
    private scrollToFocusedItem(): void {
        if (!this.pendingFocusScroll || this.focusItemId == null) return;
        this.pendingFocusScroll = false;
        const row = this.root.querySelector<HTMLElement>(`#${Views.itemAnchorId(this.focusItemId)}`);
        // Ohne Verzögerung scrollt der Browser noch gegen die eben
        // wiederhergestellte Scrollposition an. Ohne Animation: Die Zeile soll
        // sofort dastehen, nicht angefahren werden.
        if (row) window.requestAnimationFrame(() => row.scrollIntoView({ block: "center", behavior: "auto" }));
    }

    /**
     * Füllt die Ausschnitt-Vorschauen nach dem Rendern. Der Zuschnitt läuft
     * über Canvas (siehe Views.cropToDataUrl) statt über CSS-Verschiebungen —
     * damit kann die Vorschau nicht verzerren. Ergebnisse sind
     * zwischengespeichert, ein erneutes render() kostet also nichts.
     */
    /**
     * Passt die Höhe der "Sonstiges"-Felder an ihren Inhalt an — nur in
     * Browsern ohne `field-sizing: content` (siehe autoGrow).
     *
     * Muss dort nach jedem Rendern laufen: Das Feld kommt mit einer
     * geschätzten Zeilenzahl aus views.ts, und eine zu knappe Schätzung würde
     * den Text abschneiden — die Notiz war vorher frei lesbar und soll es
     * bleiben.
     */
    private fitNoteFields(scope: ParentNode = this.root): void {
        for (const field of Array.from(scope.querySelectorAll<HTMLTextAreaElement>('[data-quick-edit="sonstiges"]'))) {
            autoGrow(field);
        }
    }

    private async hydrateCropThumbs(scope: ParentNode = this.root): Promise<void> {
        // Am Datenattribut statt an der Klasse ausgewählt, damit jede
        // Ausschnitt-Vorschau erfasst wird — Miniatur in der Positionsliste
        // wie große Vorschau im Prüf-Assistenten. `scope` grenzt den Durchlauf
        // auf einen nachträglich eingesetzten Teilbaum ein (Suchvorschläge).
        // Verzögert geladene Vorschauen bleiben außen vor, um die sie sich der
        // IntersectionObserver in observeLazyCrops kümmert.
        const thumbs = Array.from(scope.querySelectorAll<HTMLElement>("[data-crop-src]:not([data-crop-lazy])"));
        // Alle Ausschnitte einer Position stammen in der Regel aus demselben
        // Foto — geteilt wird es einmal geladen statt je Zeile erneut. Der
        // Vorrat lebt nur für diesen Durchgang.
        const pool: Views.ImagePool = new Map();
        for (const thumb of thumbs) {
            await this.fillCropThumb(thumb, pool);
        }
    }

    /** Schneidet eine einzelne Vorschau zu und setzt sie ins <img>. */
    private async fillCropThumb(thumb: HTMLElement, pool: Views.ImagePool): Promise<void> {
        const img = thumb.querySelector("img");
        // Trefferlisten setzen eine kleinere Bildquelle für die Miniatur
        // (siehe Views.cropPreview); die Lightbox bleibt beim Original.
        const src = thumb.getAttribute("data-crop-thumb") || thumb.getAttribute("data-crop-src");
        if (!img || !src) return;
        const num = (name: string) => Number(thumb.getAttribute(name));
        const crop = { media: 0, x: num("data-x"), y: num("data-y"), w: num("data-w"), h: num("data-h") };

        const data = await Views.cropToDataUrl(src, crop, pool);
        // Zwischenzeitliches Neu-Rendern kann das Element ersetzt haben.
        if (!img.isConnected) return;
        // Sichtbar erst, wenn das Bild wirklich steht: Bis dahin ist das <img>
        // leer, und ein leerer Rahmen mit Platzhaltersymbol des Browsers wäre
        // beim Scrollen durch die Liste ein ständiges Aufblitzen.
        img.addEventListener("load", () => img.classList.add("is-loaded"), { once: true });
        // Ohne Zuschnitt (z. B. blockiertes Canvas) lieber das volle Foto
        // als eine leere Fläche zeigen.
        img.src = data || src;
    }

    /**
     * Lädt Ausschnitt-Vorschauen erst, wenn ihre Zeile in Sichtweite kommt.
     *
     * Die Gegenstandsliste hat eine Zeile je Gegenstand — bei knapp
     * zweitausend Zeilen würde das sofortige Füllen ebenso viele Fotos laden
     * und die Ansicht minutenlang blockieren, obwohl ein Dutzend davon
     * sichtbar ist. `rootMargin` lädt etwas im Voraus, damit beim Scrollen
     * nichts nachklappt.
     *
     * Bezugsrahmen ist bewusst das Fenster und nicht ein bestimmter
     * Scrollcontainer: Die Tabelle bringt beim Virtualisieren ihren eigenen
     * mit, die Suchvorschläge nicht — über das Fenster stimmt die Rechnung in
     * beiden Fällen.
     */
    private observeLazyCrops(scope: ParentNode = this.root): void {
        this.lazyCropObserver?.disconnect();
        this.lazyCropObserver = null;
        this.watchLazyCrops(Array.from(scope.querySelectorAll<HTMLElement>("[data-crop-lazy]")));
    }

    /**
     * Nimmt weitere Vorschauen in die Beobachtung auf, ohne die laufende
     * abzubrechen.
     *
     * Beim Scrollen kommen ständig einzelne Zeilen dazu. Würde dafür jedes Mal
     * ein neuer Beobachter entstehen, verlören die noch ausstehenden Zeilen
     * ihre Beobachtung und blieben leer.
     */
    private watchLazyCrops(elemente: HTMLElement[]): void {
        // Übergeben werden ganze Tabellenzeilen (aus der Virtualisierung) oder
        // die Vorschauen selbst (aus observeLazyCrops) — beides wird erfasst.
        const kandidaten: HTMLElement[] = [];
        for (const el of elemente) {
            if (el.matches("[data-crop-lazy]")) kandidaten.push(el);
            kandidaten.push(...Array.from(el.querySelectorAll<HTMLElement>("[data-crop-lazy]")));
        }
        // Bereits gefüllte Vorschauen bleiben unangetastet — sie erneut
        // zuzuschneiden wäre nicht nur Arbeit ohne Ergebnis, es ist genau das,
        // was beim Scrollen als Flackern sichtbar wird.
        const targets = kandidaten.filter((el) => !el.querySelector("img")?.getAttribute("src"));
        if (targets.length === 0) return;

        const pool: Views.ImagePool = new Map();
        if (typeof IntersectionObserver === "undefined") {
            // Ohne Observer lieber alles laden als nichts zeigen.
            void (async () => {
                for (const thumb of targets) await this.fillCropThumb(thumb, pool);
            })();
            return;
        }

        if (this.lazyCropObserver) {
            targets.forEach((t) => this.lazyCropObserver!.observe(t));
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    const thumb = entry.target as HTMLElement;
                    observer.unobserve(thumb);
                    void this.fillCropThumb(thumb, pool);
                }
            },
            { rootMargin: "400px 0px" }
        );
        targets.forEach((t) => observer.observe(t));
        this.lazyCropObserver = observer;
    }

    /** Gibt ausgehängte Zeilen aus der Beobachtung frei. */
    private unwatchLazyCrops(elemente: HTMLElement[]): void {
        const observer = this.lazyCropObserver;
        if (!observer) return;
        for (const el of elemente) {
            if (el.matches("[data-crop-lazy]")) observer.unobserve(el);
            el.querySelectorAll<HTMLElement>("[data-crop-lazy]").forEach((t) => observer.unobserve(t));
        }
    }

    private bindEvents(): void {
        jsidea.event.on({
            type: "click",
            scope: this.root,
            selector: "[data-action]",
            listener: ({ event, target }) => this.handleAction(event, target),
        });
        // Klick auf eine Datensatz-Nummer legt sie in die Zwischenablage.
        // Der Listener hängt in der Capture-Phase: Die Nummern stehen in
        // anklickbaren Zeilen, und die Aktionen darunter laufen erst in der
        // Bubble-Phase — so öffnet der Kopierklick nicht zugleich den Eintrag.
        this.root.addEventListener("click", (event) => {
            const knopf = (event.target as HTMLElement)?.closest?.<HTMLElement>("[data-copy-id]");
            if (!knopf) return;
            event.preventDefault();
            event.stopPropagation();
            const id = knopf.getAttribute("data-copy-id") || "";
            void copyText(`#${id}`).then((ok) => flashCopiedId(knopf, ok));
        }, true);
        // Shift-Klick dient in den Listen der Bereichsauswahl — ohne das
        // preventDefault würde der Browser dabei Zellentext blau markieren.
        this.root.addEventListener("mousedown", (event) => {
            if (event.shiftKey && (event.target as HTMLElement).closest?.('tr[data-action^="open-"]')) {
                event.preventDefault();
            }
        });
        // Escape hebt die Mehrfachauswahl auf — von überall, auch wenn der
        // Fokus gerade in einem Filterfeld steckt. Andere Escape-Nutzer
        // (Dialoge, Lightbox) laufen vorher über eigene Handler; eine leere
        // Auswahl lässt das Ereignis unangetastet.
        document.addEventListener("keydown", (event) => {
            if (event.key !== "Escape") return;
            if (!this.listSelection.kind || this.listSelection.ids.size === 0) return;
            const kind = this.listSelection.kind;
            this.listSelection = { kind: null, ids: new Set(), anchor: null };
            this.refreshSelectionList(kind);
        });
        jsidea.event.on({
            type: "submit",
            scope: this.root,
            selector: "form[data-form]",
            listener: ({ event, target }) => this.handleSubmit(event as SubmitEvent, target as HTMLFormElement),
        });
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: "[data-filter],[data-room-filter],[data-item-filter],[data-note-filter],[data-compare-filter]",
            listener: ({ target }) => this.handleFilterChange(target),
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: "[data-filter],[data-room-filter],[data-item-filter],[data-note-filter],[data-compare-filter]",
            listener: ({ target }) => this.handleFilterChange(target),
        });
        // "input" statt "change" und ohne render(): Der Instanzname soll
        // sofort im Browser-Tab greifen, während getippt wird — ein
        // Neuaufbau der Ansicht würde den Cursor aus dem Feld werfen.
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: '[data-action="set-instance-label"]',
            listener: ({ target }) => {
                this.instanceLabel = (target as HTMLInputElement).value;
                writeStored(INSTANCE_LABEL_KEY, this.instanceLabel);
                this.applyDocumentTitle();
            },
        });
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: "[data-dup-search]",
            listener: ({ target }) => this.handleDuplicateSearch(target as HTMLInputElement),
        });
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: "[data-global-search]",
            listener: ({ target }) => this.handleGlobalSearch(target as HTMLInputElement),
        });
        jsidea.event.on({
            type: "keydown",
            scope: this.root,
            selector: "[data-global-search]",
            listener: ({ event }) => this.handleSearchKey(event as KeyboardEvent),
        });
        // Wer ins Suchfeld klickt, in dem noch etwas steht, will die Treffer
        // dazu sehen — sie waren beim letzten Klick daneben nur weggeklappt
        // worden. Ohne das müsste man erst ein Zeichen tippen und wieder
        // löschen, damit die Liste zurückkommt. "focusin" statt "focus", weil
        // nur das aufsteigt und die Zustellung hier über die Wurzel läuft.
        jsidea.event.on({
            type: "focusin",
            scope: this.root,
            selector: "[data-global-search]",
            listener: ({ target }) => this.openSuggestions(target as HTMLInputElement),
        });
        jsidea.event.on({
            type: "click",
            scope: this.root,
            selector: "[data-global-search]",
            listener: ({ target }) => this.openSuggestions(target as HTMLInputElement),
        });
        // Offene Trefferlisten schließen, sobald woanders hingeklickt wird:
        // Sie liegen über dem Inhalt und würden sonst das verdecken, was
        // darunter steht — auch nachdem die Suche längst erledigt ist.
        this.root.addEventListener("click", (event) => {
            const clicked = event.target as HTMLElement;
            for (const box of Array.from(this.root.querySelectorAll<HTMLElement>("[data-dup-suggest]"))) {
                if (box.hidden || box.closest("[data-dup-row]")?.contains(clicked)) continue;
                box.hidden = true;
            }
            const suggest = this.root.querySelector<HTMLElement>("[data-search-suggest]");
            if (suggest && !suggest.hidden && !suggest.closest("[data-search-box]")?.contains(clicked)) {
                suggest.hidden = true;
            }
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: "[data-upload]",
            listener: ({ target }) => this.handleUpload(target as HTMLInputElement),
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: "[data-quick-edit]",
            listener: ({ target }) => void this.handleQuickEdit(target as HTMLInputElement | HTMLTextAreaElement),
        });
        jsidea.event.on({
            type: "keydown",
            scope: this.root,
            selector: "[data-quick-edit]",
            listener: ({ event, target }) => {
                // Im mehrzeiligen "Sonstiges" gehoert Enter zum Text; dort wird
                // beim Verlassen des Feldes gespeichert wie ueberall sonst auch.
                if ((event as KeyboardEvent).key === "Enter" && !(target instanceof HTMLTextAreaElement)) {
                    event.preventDefault();
                    (target as HTMLInputElement).blur();
                }
            },
        });
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: '[data-quick-edit="sonstiges"]',
            listener: ({ target }) => autoGrow(target as HTMLTextAreaElement),
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-scope"]',
            listener: ({ target }) => {
                this.exportScope = (target as HTMLSelectElement).value as "alle" | "geprueft";
                this.render();
            },
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-room-mode"]',
            listener: ({ target }) => {
                this.exportRoomMode = (target as HTMLSelectElement).value as App["exportRoomMode"];
                this.render();
            },
        });
        jsidea.event.on({
            // "change" statt "input": Bei jedem Tastendruck neu zu rendern
            // würde das Feld ersetzen und den Fokus verlieren. Der Wert wird
            // ohnehin erst beim Export gebraucht.
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-owner"]',
            listener: ({ target }) => {
                this.exportOwner = (target as HTMLInputElement).value;
                writeStored(EXPORT_OWNER_KEY, this.exportOwner);
            },
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-place"]',
            listener: ({ target }) => {
                this.exportPlace = (target as HTMLInputElement).value;
                writeStored(EXPORT_PLACE_KEY, this.exportPlace);
            },
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-date"]',
            listener: ({ target }) => {
                this.exportDate = (target as HTMLInputElement).value;
            },
        });
        // "input" statt "change" und ohne render(): Der Text wird beim Tippen
        // gesichert, ein Neuaufbau der Ansicht würde dabei den Cursor aus dem
        // Feld werfen.
        jsidea.event.on({
            type: "input",
            scope: this.root,
            selector: '[data-action="set-export-intro-text"]',
            listener: ({ target }) => {
                this.exportIntroText = (target as HTMLTextAreaElement).value;
                writeStored(EXPORT_INTRO_KEY, this.exportIntroText);
            },
        });
        jsidea.event.on({
            type: "change",
            scope: this.root,
            selector: '[data-action="set-export-value-mode"]',
            listener: ({ target }) => {
                this.exportValueMode = (target as HTMLSelectElement).value as App["exportValueMode"];
                this.render();
            },
        });
        jsidea.event.on({
            type: "dragover",
            scope: this.root,
            selector: "[data-dropzone]",
            listener: ({ event, target }) => {
                event.preventDefault();
                target.classList.add("hi-dropzone--drag");
            },
        });
        jsidea.event.on({
            type: "dragleave",
            scope: this.root,
            selector: "[data-dropzone]",
            listener: ({ target }) => {
                target.classList.remove("hi-dropzone--drag");
            },
        });
        jsidea.event.on({
            type: "drop",
            scope: this.root,
            selector: "[data-dropzone]",
            listener: ({ event, target }) => {
                event.preventDefault();
                target.classList.remove("hi-dropzone--drag");
                const owner = target.getAttribute("data-dropzone");
                const ownerId = Number(target.getAttribute("data-id"));
                const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
                if (owner) void this.uploadFiles(owner, ownerId, files);
            },
        });
    }

    /**
     * Zeilen der Shop-Link-Liste hinzufügen, entfernen und einen Preis als
     * Wert je Stück übernehmen.
     *
     * Arbeitet ausschließlich am DOM und ruft bewusst kein render() auf: Die
     * App rendert immer komplett neu, ein Neuaufbau würde also alles
     * verwerfen, was im offenen Formular schon getippt wurde.
     */
    private handleShopLinkAction(action: string, target: HTMLElement): void {
        const field = target.closest<HTMLElement>(".hi-shop-field");
        const rows = field?.querySelector<HTMLElement>("[data-shop-rows]");
        if (!rows) return;

        if (action === "add-shop-link") {
            rows.insertAdjacentHTML("beforeend", Views.shopLinkRow());
            rows.querySelector<HTMLInputElement>("[data-shop-row]:last-child .hi-shop-row__url")?.focus();
            return;
        }

        const row = target.closest<HTMLElement>("[data-shop-row]");
        if (!row) return;

        if (action === "use-shop-price") {
            const preis = row.querySelector<HTMLInputElement>(".hi-shop-row__price")?.value.trim();
            const wert = target.closest("form")?.querySelector<HTMLInputElement>('[name="wert"]');
            if (preis && wert) {
                wert.value = preis;
                wert.focus();
            }
            return;
        }

        row.remove();
        // Die Liste bleibt bedienbar: Ohne verbleibende Zeile gäbe es kein
        // Eingabefeld mehr, in das ein neues Angebot geschrieben werden kann.
        if (!rows.querySelector("[data-shop-row]")) {
            rows.insertAdjacentHTML("beforeend", Views.shopLinkRow());
        }
    }

    /** IDs aus dem `data-group`-Attribut eines Duplikat-Knopfs. */
    private duplicateGroupIds(target: HTMLElement): number[] {
        return (target.getAttribute("data-group") || "")
            .split(",")
            .map((s) => Number(s.trim()))
            .filter((n) => Number.isFinite(n) && n > 0);
    }

    /**
     * Schaltet einen einzelnen Eintrag eines Duplikat-Falls um.
     *
     * Ein Fall kann mehr als zwei Beteiligte haben — liegt dieselbe Sache auf
     * vier Fotos, sind drei davon überzählig. Deshalb wird hier je Eintrag
     * entschieden statt paarweise. Die eine Regel dabei: **Mindestens ein
     * Eintrag muss gezählt bleiben.** Wären alle als Duplikat markiert, fiele
     * die Sache vollständig aus der Aufstellung — der Versicherte bekäme für
     * etwas, das er nachweislich besitzt, gar nichts.
     */
    private async handleDuplicateMark(target: HTMLElement): Promise<void> {
        const id = Number(target.getAttribute("data-id"));
        const item = this.items.find((i) => i.id === id);
        if (!item) return;

        const group = this.duplicateGroupIds(target)
            .map((gid) => this.items.find((i) => i.id === gid))
            .filter((i): i is Item => !!i);

        const aktiv = item.duplikatStatus === "bestaetigt";
        if (!aktiv && group.filter((i) => i.duplikatStatus !== "bestaetigt").length <= 1) return;

        // Voller Datensatz wie bei toggle-geprueft: ACF verlangt seine
        // required-Felder auch bei Teil-Updates.
        this.applyItems(await Api.updateItem(item.id, {
            ...item,
            duplikatStatus: aktiv ? "kein_duplikat" : "bestaetigt",
        }));
        this.render();
    }

    /**
     * Setzt alle Einträge eines Duplikat-Falls auf denselben Status.
     *
     * `reopen` stellt den Fall wieder auf „möglich": Ein Klick auf ein bereits
     * gesetztes „Kein Duplikat" nimmt die Entscheidung zurück, statt sie
     * wirkungslos zu wiederholen. Ohne das führte aus einem versehentlich
     * abgehakten Fall nur „Verdacht löschen" heraus — und das verwirft den
     * Verdacht samt Begründung.
     */
    private async handleDuplicateGroup(target: HTMLElement): Promise<void> {
        const mode = target.getAttribute("data-mode");
        const status: DuplicateStatus =
            mode === "none" ? "kein_duplikat" : mode === "reopen" ? "moeglich" : "";
        const group = this.duplicateGroupIds(target)
            .map((gid) => this.items.find((i) => i.id === gid))
            .filter((i): i is Item => !!i);

        this.applyItems(...await inParallel(group, 4, (item) => Api.updateItem(item.id, { ...item, duplikatStatus: status })));
        this.render();
    }

    /**
     * Verweiszeilen des Duplikat-Felds hinzufügen, entfernen und ein Ziel aus
     * der Vorschlagsliste übernehmen.
     *
     * Arbeitet wie handleShopLinkAction nur am DOM: Ein render() würde das
     * offene Formular samt getippter Eingaben verwerfen.
     */
    private handleDuplicateRefAction(action: string, target: HTMLElement): void {
        const field = target.closest<HTMLElement>("[data-dup-field]");
        const rows = field?.querySelector<HTMLElement>("[data-dup-rows]");
        if (!rows) return;

        if (action === "add-dup-ref") {
            rows.insertAdjacentHTML("beforeend", Views.duplicateRefRow());
            rows.querySelector<HTMLInputElement>("[data-dup-row]:last-child .hi-dup-row__search")?.focus();
            return;
        }

        const row = target.closest<HTMLElement>("[data-dup-row]");
        if (!row) return;

        if (action === "pick-dup-item") {
            const itemId = target.getAttribute("data-item-id") || "0";
            const positionId = target.getAttribute("data-position-id") || "0";
            const label = target.getAttribute("data-label") || "";
            row.querySelector<HTMLInputElement>('[name="dup_item"]')!.value = itemId;
            row.querySelector<HTMLInputElement>('[name="dup_position"]')!.value = positionId;
            const search = row.querySelector<HTMLInputElement>(".hi-dup-row__search");
            if (search) search.value = label;
            const suggest = row.querySelector<HTMLElement>("[data-dup-suggest]");
            if (suggest) {
                suggest.innerHTML = "";
                suggest.hidden = true;
            }
            row.querySelector<HTMLInputElement>(".hi-dup-row__note")?.focus();
            return;
        }

        row.remove();
        // Ohne verbleibende Zeile gäbe es kein Feld mehr, in das ein neuer
        // Verweis geschrieben werden kann.
        if (!rows.querySelector("[data-dup-row]")) {
            rows.insertAdjacentHTML("beforeend", Views.duplicateRefRow());
        }
    }

    /** Live-Trefferliste unter dem Suchfeld einer Verweiszeile. */
    private handleDuplicateSearch(input: HTMLInputElement): void {
        const row = input.closest<HTMLElement>("[data-dup-row]");
        const suggest = row?.querySelector<HTMLElement>("[data-dup-suggest]");
        if (!row || !suggest) return;

        // Wer neu tippt, meint nicht mehr das zuvor gewählte Ziel — die IDs
        // müssen weg, sonst bliebe ein Verweis auf einen anderen Gegenstand
        // stehen, als im Feld sichtbar ist.
        row.querySelector<HTMLInputElement>('[name="dup_item"]')!.value = "0";
        row.querySelector<HTMLInputElement>('[name="dup_position"]')!.value = "0";

        const self = Number(row.closest<HTMLElement>("[data-dup-field]")?.getAttribute("data-item-id") || 0);
        suggest.innerHTML = Views.duplicateSuggestions(this, input.value, self);
        suggest.hidden = input.value.trim() === "";
    }

    /**
     * Live-Suche im Kopf der Seitenleiste.
     *
     * Baut nur die Vorschlagsliste neu auf, nie die ganze Ansicht: Ein
     * render() würde das Eingabefeld ersetzen und damit den Fokus samt
     * Cursorposition mitten im Tippen wegnehmen.
     */
    private handleGlobalSearch(input: HTMLInputElement): void {
        this.searchQuery = input.value;
        this.openSuggestions(input);

        // Steht die Ergebnisseite schon offen, wandert sie beim Tippen mit.
        if (this.view === "search") {
            const content = this.root.querySelector<HTMLElement>("#hi-content");
            if (content) content.innerHTML = Views.renderSearch(this);
            this.replaceUrl();
            if (content) void this.hydrateCropThumbs(content);
        }
    }

    /**
     * Baut die Trefferliste unter dem Suchfeld auf und klappt sie auf.
     *
     * Zu einem leeren Feld gibt es nichts zu zeigen — die Liste bliebe sonst
     * beim Hineinklicken als leerer Kasten über dem Inhalt stehen.
     */
    private openSuggestions(input: HTMLInputElement): void {
        const suggest = this.root.querySelector<HTMLElement>("[data-search-suggest]");
        if (!suggest) return;
        if (input.value.trim() === "") {
            suggest.hidden = true;
            return;
        }
        suggest.innerHTML = Views.renderSearchSuggestions(this, input.value);
        suggest.hidden = false;
        this.placeSuggestions(input, suggest);
        // Die Ausschnitt-Vorschauen der Vorschläge sind noch leere Hüllen —
        // gefüllt werden sie sonst nur nach einem render().
        void this.hydrateCropThumbs(suggest);
    }

    /**
     * Setzt die Vorschlagsliste unter das Suchfeld.
     *
     * Die Liste liegt `position: fixed` und wird hier von Hand platziert: Die
     * Seitenleiste ist ein eigener Scrollbereich (`overflow-y: auto`) und
     * würde ein absolut positioniertes Panel an ihrem Rand abschneiden. Fest
     * verdrahtete Koordinaten im Stylesheet wären zu starr — die Leiste liegt
     * auf schmalen Schirmen quer über dem Inhalt statt links daneben.
     */
    private placeSuggestions(input: HTMLElement, suggest: HTMLElement): void {
        const rect = input.getBoundingClientRect();
        const rand = 12;
        // Breiter als das Feld, aber nie über den Fensterrand hinaus: Ein
        // Treffer braucht Platz für Bild, Fundort und Fundstelle.
        const breite = Math.min(Math.max(rect.width, 420), window.innerWidth - 2 * rand);
        const links = Math.min(rect.left, window.innerWidth - breite - rand);
        suggest.style.top = `${rect.bottom + 4}px`;
        suggest.style.left = `${Math.max(rand, links)}px`;
        suggest.style.width = `${breite}px`;
        suggest.style.maxHeight = `${Math.max(200, window.innerHeight - rect.bottom - 2 * rand)}px`;
    }

    /** Enter öffnet die Ergebnisseite, Escape schließt die Vorschläge. */
    private handleSearchKey(event: KeyboardEvent): void {
        if (event.key === "Escape") {
            const suggest = this.root.querySelector<HTMLElement>("[data-search-suggest]");
            if (suggest) suggest.hidden = true;
            return;
        }
        if (event.key !== "Enter") return;
        event.preventDefault();
        this.openSearchPage();
    }

    /**
     * Wechselt auf die Ergebnisseite und gibt den Fokus zurück ins Suchfeld —
     * nach dem render() ist das Feld ein neues Element, ohne diesen Schritt
     * müsste man für jede Verfeinerung erneut hineinklicken.
     */
    private openSearchPage(): void {
        if (this.searchQuery.trim().length < 2) return;
        this.setView("search");
        const input = this.root.querySelector<HTMLInputElement>("[data-global-search]");
        if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
        // Der Fokus oben hat die Vorschlagsliste wieder aufgeklappt (siehe
        // openSuggestions). Auf der Ergebnisseite liegt sie genau über dem,
        // was gerade geöffnet werden sollte — dieselben Treffer, nur kürzer.
        const suggest = this.root.querySelector<HTMLElement>("[data-search-suggest]");
        if (suggest) suggest.hidden = true;
    }

    private handleFilterChange(target: HTMLElement): void {
        // Raumliste zuerst: Sie nutzt ein eigenes Attribut, damit ihr
        // Suchfeld nicht den Filter der Positionsliste überschreibt.
        const roomKey = target.getAttribute("data-room-filter") as keyof App["roomFilter"] | null;
        if (roomKey) {
            this.roomFilter[roomKey] = (target as HTMLInputElement).value;
            this.refreshList("#hi-room-results", Views.renderRoomsListResults(this));
            return;
        }

        const itemKey = target.getAttribute("data-item-filter") as keyof App["itemFilter"] | null;
        if (itemKey) {
            this.itemFilter[itemKey] = (target as HTMLInputElement | HTMLSelectElement).value;
            this.refreshList("#hi-item-results", Views.renderItemsListResults(this));
            return;
        }

        const noteKey = target.getAttribute("data-note-filter") as keyof App["noteFilter"] | null;
        if (noteKey) {
            this.noteFilter[noteKey] = (target as HTMLInputElement | HTMLSelectElement).value;
            this.refreshList("#hi-note-results", Views.renderNotesResults(this));
            return;
        }

        const compareKey = target.getAttribute("data-compare-filter") as keyof App["compareFilter"] | null;
        if (compareKey) {
            this.compareFilter[compareKey] = (target as HTMLInputElement | HTMLSelectElement).value;
            this.compareLimit = 200;
            this.refreshList("#hi-compare-results", Views.renderCompareResults(this));
            return;
        }

        const key = target.getAttribute("data-filter") as keyof App["listFilter"] | null;
        if (!key) return;
        this.listFilter[key] = (target as HTMLInputElement | HTMLSelectElement).value;
        this.refreshList("#hi-list-results", Views.renderPositionsListResults(this));
    }

    /**
     * Vergisst die gemerkte Scrollposition der aktuellen Liste.
     *
     * Nach dem Umsortieren steht an derselben Stelle etwas völlig anderes;
     * der Zeile zu folgen, die vorher oben stand, würde mitten in die neue
     * Reihenfolge springen. Erwartet wird der Anfang der Liste.
     */
    private resetListScroll(): void {
        // Nur ein Merker: Das folgende render() sichert die Position zuerst
        // und würde ein bloßes Löschen sofort wieder überschreiben.
        this.listScrollReset = true;
    }

    /**
     * Tauscht nur den Ergebnisbereich einer Liste aus — ohne render(), damit
     * das Filterfeld den Fokus behält.
     *
     * innerHTML statt outerHTML: outerHTML ersetzt den Knoten mit der ID
     * selbst, wodurch er ab der zweiten Filteränderung nicht mehr auffindbar
     * war (der Filter wirkte nur beim allerersten Mal). Danach muss die
     * Tabelle neu bestückt werden, denn die Hülle kommt ohne Zeilen.
     */
    private refreshList(selector: string, html: string): void {
        const container = this.root.querySelector<HTMLElement>(selector);
        if (!container) return;
        container.innerHTML = html;
        this.mountVirtualTables(container);
    }

    private async handleAction(event: Event, target: HTMLElement): Promise<void> {
        if (this.handleListSelection(event, target)) return;
        try {
            await withBusy(target, () => this.handleActionSwitch(target));
        } catch (error) {
            await alertDialog("Aktion fehlgeschlagen", error instanceof Error ? error.message : String(error));
        }
    }

    /**
     * Mehrfachauswahl in den Listen: Strg/Cmd toggelt die Zeile, Shift markiert
     * den Bereich vom Anker bis zur geklickten Zeile (Strg+Shift ergänzt ihn,
     * Shift allein ersetzt die Auswahl durch den Bereich — wie im Dateimanager).
     *
     * Gibt true zurück, wenn der Klick eine Auswahl war: Dann öffnet er keine
     * Detailansicht. Greift nur auf den Tabellenzeilen der drei Listen — die
     * Knöpfe in den Zellen (Position/Raum öffnen, Aufklappen) bleiben Knöpfe.
     */
    private handleListSelection(event: Event, target: HTMLElement): boolean {
        const maus = event as MouseEvent;
        if (!maus.ctrlKey && !maus.metaKey && !maus.shiftKey) return false;
        if (!(target instanceof HTMLTableRowElement)) return false;
        const kind = ({ "open-room": "rooms", "open-position": "positions", "open-item": "items" } as
            Record<string, Views.VirtualList | undefined>)[target.getAttribute("data-action") ?? ""];
        const id = Number(target.getAttribute("data-id"));
        if (!kind || !id) return false;

        event.preventDefault();
        const auswahl = this.listSelection;
        if (auswahl.kind !== kind) {
            auswahl.kind = kind;
            auswahl.ids = new Set();
            auswahl.anchor = null;
        }
        if (maus.shiftKey) {
            // Bereich in der Reihenfolge, die die Liste gerade anzeigt —
            // also nach aktuellem Filter und aktueller Sortierung.
            const reihenfolge = this.listSelectionOrder(kind);
            let von = reihenfolge.indexOf(auswahl.anchor ?? id);
            const bis = reihenfolge.indexOf(id);
            if (von === -1) von = bis;
            if (bis !== -1) {
                if (!maus.ctrlKey && !maus.metaKey) auswahl.ids = new Set();
                for (const x of reihenfolge.slice(Math.min(von, bis), Math.max(von, bis) + 1)) auswahl.ids.add(x);
            }
            if (auswahl.anchor == null) auswahl.anchor = id;
        } else {
            if (auswahl.ids.has(id)) auswahl.ids.delete(id);
            else auswahl.ids.add(id);
            auswahl.anchor = id;
        }
        this.refreshSelectionList(kind);
        return true;
    }

    /** IDs der Liste in Anzeige-Reihenfolge (Filter + Sortierung angewendet). */
    private listSelectionOrder(kind: Views.VirtualList): number[] {
        if (kind === "rooms") return Views.filteredRooms(this).map((r) => r.id);
        if (kind === "positions") return Views.filteredPositions(this).map((p) => p.id);
        return Views.filteredItems(this).map((i) => i.id);
    }

    /**
     * Baut den Ergebnisbereich der Liste neu auf und stellt die Scrollposition
     * wieder her — ein Auswahl-Klick mitten in einer langen Liste darf nicht
     * an den Anfang springen. Der Neuaufbau ist nötig, weil die virtuelle
     * Tabelle ihre Zeilen-HTML beim Einhängen einfriert; nur so tragen auch
     * Zeilen außerhalb des Sichtfensters die Markierung.
     */
    private refreshSelectionList(kind: Views.VirtualList): void {
        const ziel = {
            rooms: { selector: "#hi-room-results", html: () => Views.renderRoomsListResults(this) },
            positions: { selector: "#hi-list-results", html: () => Views.renderPositionsListResults(this) },
            items: { selector: "#hi-item-results", html: () => Views.renderItemsListResults(this) },
        }[kind];
        const scroller = this.root.querySelector<HTMLElement>(`${ziel.selector} [data-table-scroll]`);
        const top = scroller?.scrollTop ?? 0;
        this.refreshList(ziel.selector, ziel.html());
        const neu = this.root.querySelector<HTMLElement>(`${ziel.selector} [data-table-scroll]`);
        if (neu) neu.scrollTop = top;
    }

    private async handleActionSwitch(target: HTMLElement): Promise<void> {
        const action = target.getAttribute("data-action");
        const idAttr = target.getAttribute("data-id");
        const id = idAttr ? Number(idAttr) : null;

        switch (action) {
            case "nav":
                this.setView((target.getAttribute("data-view") as ViewName) || "dashboard");
                break;
            case "compare-run":
                await this.runCompare();
                break;
            case "compare-edit":
                // Zugang erneut zeigen, ohne ihn zu verlieren: Das Formular
                // steht wieder offen, das Passwortfeld bleibt leer.
                this.compareEditOpen = true;
                this.render();
                break;
            case "compare-cancel-edit":
                this.compareEditOpen = false;
                this.render();
                break;
            case "compare-forget":
                await this.forgetReference();
                break;
            case "compare-more":
                this.compareLimit += 200;
                this.refreshList("#hi-compare-results", Views.renderCompareResults(this));
                break;
            case "compare-minor":
                this.compareShowMinor = !this.compareShowMinor;
                writeStored(COMPARE_MINOR_KEY, this.compareShowMinor ? "1" : "0");
                this.render();
                break;
            case "compare-category": {
                const wert = target.getAttribute("data-value") || "";
                this.compareFilter.kategorie = this.compareFilter.kategorie === wert ? "" : wert;
                this.compareLimit = 200;
                this.refreshList("#hi-compare-results", Views.renderCompareResults(this));
                break;
            }
            case "mcp-reveal": {
                const uuid = target.getAttribute("data-uuid") || "";
                if (this.mcpRevealed.has(uuid)) {
                    this.mcpRevealed.delete(uuid);
                } else {
                    this.mcpRevealed.add(uuid);
                }
                this.render();
                break;
            }
            case "mcp-copy-prompt":
            case "mcp-copy-command":
            case "mcp-copy-config": {
                const uuid = target.getAttribute("data-uuid") || "";
                const cred = this.mcpCredentials?.find((c) => c.uuid === uuid);
                if (!cred) break;
                const text = action === "mcp-copy-prompt"
                    ? Views.buildMcpPrompt(cred)
                    : action === "mcp-copy-command"
                        ? Views.buildMcpClaudeCommand(cred)
                        : Views.buildMcpDesktopConfig(cred);
                flashCopied(target, await copyText(text));
                break;
            }
            case "mcp-delete": {
                const uuid = target.getAttribute("data-uuid") || "";
                const cred = this.mcpCredentials?.find((c) => c.uuid === uuid);
                if (!cred) break;
                const weiter = await confirmDialog(
                    "Verbindung widerrufen",
                    `Die Verbindung „${cred.name}" wird widerrufen. Eine KI, die diesen Zugang nutzt, verliert sofort den Zugriff.`,
                    { confirmLabel: "Widerrufen" }
                );
                if (!weiter) break;
                await Api.deleteMcpCredential(uuid);
                this.mcpCredentials = (this.mcpCredentials ?? []).filter((c) => c.uuid !== uuid);
                this.mcpRevealed.delete(uuid);
                this.render();
                break;
            }
            case "add-position":
                this.newPositionPhotos = [];
                this.setView("form");
                break;
            case "open-position":
                if (id != null) this.setView("position", id);
                break;
            case "open-item":
                if (id != null) this.openItem(id);
                break;
            case "clear-selection": {
                const kind = this.listSelection.kind;
                this.listSelection = { kind: null, ids: new Set(), anchor: null };
                if (kind) this.refreshSelectionList(kind);
                break;
            }
            case "add-shop-link":
            case "remove-shop-link":
            case "use-shop-price":
                this.handleShopLinkAction(action, target);
                break;
            case "add-dup-ref":
            case "remove-dup-ref":
            case "pick-dup-item":
                this.handleDuplicateRefAction(action, target);
                break;
            case "run-search":
                this.openSearchPage();
                break;
            case "open-search-hit":
                if (id != null) {
                    const hitItem = target.getAttribute("data-item-id");
                    this.setView(
                        (target.getAttribute("data-view") as ViewName) || "position",
                        id,
                        hitItem ? Number(hitItem) : null
                    );
                }
                break;
            case "open-duplicate":
                // data-id ist die Position, data-item-id der Gegenstand darin —
                // die Position allein würde nur die Liste öffnen, ohne dass
                // erkennbar wäre, welche Zeile gemeint ist.
                if (id != null) {
                    const itemAttr = target.getAttribute("data-item-id");
                    this.setView("position", id, itemAttr ? Number(itemAttr) : null);
                }
                break;
            case "set-duplicate-status":
                if (id != null) {
                    const dupItem = this.items.find((i) => i.id === id);
                    if (dupItem) {
                        const status = (target.getAttribute("data-value") || "") as DuplicateStatus;
                        // Voller Datensatz wie bei toggle-geprueft: ACF verlangt
                        // seine required-Felder auch bei Teil-Updates.
                        this.applyItems(await Api.updateItem(id, { ...dupItem, duplikatStatus: status }));
                        this.render();
                    }
                }
                break;
            case "toggle-duplicate-mark":
                await this.handleDuplicateMark(target);
                break;
            case "set-duplicate-group":
                await this.handleDuplicateGroup(target);
                break;
            case "filter-notes":
                this.noteFilter.level = target.getAttribute("data-level") || "";
                this.render();
                break;
            case "notes-show-all":
                this.noteFilter = { level: "", q: "", state: "" };
                this.render();
                break;
            case "note-add":
                this.editingNoteId = -1;
                this.render();
                break;
            case "note-edit":
                if (id != null) {
                    this.editingNoteId = id;
                    this.render();
                }
                break;
            case "note-cancel":
                this.editingNoteId = null;
                this.render();
                break;
            case "note-toggle":
                if (id != null) {
                    const note = this.notes.find((n) => n.id === id);
                    if (note) {
                        this.applyNote(await Api.updateNote(id, { erledigt: !note.erledigt }));
                        this.render();
                    }
                }
                break;
            case "note-delete":
                if (id != null) {
                    const doomed = this.notes.find((n) => n.id === id);
                    if (doomed && await confirmDialog("Hinweis löschen", `„${doomed.title}" wirklich löschen?`)) {
                        await Api.deleteNote(id);
                        this.dropNote(id);
                        this.render();
                    }
                }
                break;
            case "open-room":
                if (id != null) this.setView("room", id);
                break;
            // Aus einem Diagramm der Übersicht in die Liste dahinter: Das
            // Segment beantwortet "wie viel", die gefilterte Liste "was genau".
            case "filter-items":
                this.itemFilter = {
                    q: "",
                    raum: target.getAttribute("data-raum") || "",
                    quelle: target.getAttribute("data-quelle") || "",
                    state: target.getAttribute("data-state") || "",
                };
                this.setView("items");
                break;
            case "open-positions-for-room":
                if (id != null) {
                    this.listFilter.raum = String(id);
                    this.listFilter.q = "";
                    this.setView("positions");
                }
                break;
            case "edit-position":
                this.editingPosition = true;
                this.render();
                break;
            case "cancel-edit-position":
                this.editingPosition = false;
                this.render();
                break;
            case "edit-room":
                this.editingRoom = true;
                this.render();
                break;
            case "cancel-edit-room":
                this.editingRoom = false;
                this.render();
                break;
            case "add-item":
                this.addingItem = true;
                this.newItemPhotos = [];
                this.render();
                break;
            case "cancel-add-item":
                this.addingItem = false;
                this.newItemPhotos = [];
                this.render();
                break;
            case "edit-item-inline":
                if (id != null) this.editingItemId = id;
                this.render();
                break;
            case "cancel-edit-item-inline":
                this.editingItemId = null;
                this.render();
                break;
            case "delete-item":
                if (id != null) {
                    const item = this.items.find((i) => i.id === id);
                    const label = item ? `„${item.title}“` : "diesen Gegenstand";
                    const ok = await confirmDialog(
                        "Gegenstand löschen",
                        `Möchten Sie ${label} wirklich löschen? Anzahl, Wert, Fotos und der Prüfstatus dieses Gegenstands gehen dabei unwiderruflich verloren.`,
                        { confirmLabel: "Gegenstand löschen" }
                    );
                    if (ok) {
                        await Api.deleteItem(id);
                        this.dropItems([id]);
                        this.render();
                    }
                }
                break;
            case "delete-position":
                if (id != null) {
                    await this.deletePositionCascade(id);
                }
                break;
            case "delete-room":
                if (id != null) {
                    const room = this.rooms.find((r) => r.id === id);
                    const label = room ? `„${room.title}“` : "diesen Raum";
                    const roomPositionCount = this.positions.filter((p) => p.raum === id).length;
                    if (roomPositionCount > 0) {
                        await alertDialog(
                            "Raum kann nicht gelöscht werden",
                            `Der Raum ${label} enthält noch ${roomPositionCount} Position(en). Verschieben oder löschen Sie diese zuerst, bevor Sie den Raum selbst löschen.`
                        );
                    } else {
                        const ok = await confirmDialog(
                            "Raum löschen",
                            `Möchten Sie den Raum ${label} wirklich löschen? Beschreibung und Fotos des Raums gehen dabei unwiderruflich verloren.`,
                            { confirmLabel: "Raum löschen" }
                        );
                        if (ok) {
                            await Api.deleteRoom(id);
                            this.dropRoom(id);
                            this.setView("rooms");
                        }
                    }
                }
                break;
            case "toggle-position-items":
                if (id != null) {
                    if (this.expandedPositions.has(id)) {
                        this.expandedPositions.delete(id);
                    } else {
                        this.expandedPositions.add(id);
                    }
                    this.render();
                }
                break;
            case "toggle-all-position-items": {
                const checked = (target as HTMLInputElement).checked;
                this.expandedPositions = checked ? new Set(this.positions.map((p) => p.id)) : new Set();
                this.render();
                break;
            }
            case "sort-positions": {
                const key = target.getAttribute("data-sort-key") as App["listSort"]["key"] | null;
                if (key) {
                    this.listSort = this.listSort.key === key
                        ? { key, dir: this.listSort.dir === "asc" ? "desc" : "asc" }
                        : { key, dir: "asc" };
                    writeSortPreference("positions", this.listSort);
                    this.resetListScroll();
                    this.render();
                }
                break;
            }
            case "toggle-room-positions":
                if (id != null) {
                    if (this.expandedRooms.has(id)) {
                        this.expandedRooms.delete(id);
                    } else {
                        this.expandedRooms.add(id);
                    }
                    this.render();
                }
                break;
            case "toggle-all-room-positions": {
                const checked = (target as HTMLInputElement).checked;
                this.expandedRooms = checked ? new Set(this.rooms.map((r) => r.id)) : new Set();
                this.render();
                break;
            }
            case "sort-rooms": {
                const key = target.getAttribute("data-sort-key") as App["roomSort"]["key"] | null;
                if (key) {
                    this.roomSort = this.roomSort.key === key
                        ? { key, dir: this.roomSort.dir === "asc" ? "desc" : "asc" }
                        : { key, dir: "asc" };
                    writeSortPreference("rooms", this.roomSort);
                    this.resetListScroll();
                    this.render();
                }
                break;
            }
            case "toggle-virtual": {
                const liste = target.getAttribute("data-list") as Views.VirtualList | null;
                if (liste && liste in this.virtualLists) {
                    const an = (target as HTMLInputElement).checked;
                    this.virtualLists[liste] = an;
                    writeStored(VIRTUAL_KEY + liste, an ? "1" : "0");
                    // Der Umbau betrifft die ganze Tabelle, nicht nur ihren
                    // Rumpf — deshalb ein vollständiges Rendern.
                    this.render();
                }
                break;
            }
            case "sort-items": {
                const key = target.getAttribute("data-sort-key") as App["itemSort"]["key"] | null;
                if (key) {
                    // Zahlenspalten starten absteigend: Wer nach "Gesamt"
                    // sortiert, sucht die teuersten Zeilen, nicht die
                    // Ein-Euro-Posten. Bei Text ist A–Z das Erwartete.
                    const zahl = ["anzahl", "wert", "gesamt"].includes(key);
                    this.itemSort = this.itemSort.key === key
                        ? { key, dir: this.itemSort.dir === "asc" ? "desc" : "asc" }
                        : { key, dir: zahl ? "desc" : "asc" };
                    writeSortPreference("items", this.itemSort);
                    this.resetListScroll();
                    this.render();
                }
                break;
            }
            case "toggle-geprueft":
                if (id != null) {
                    const item = this.items.find((i) => i.id === id);
                    if (item) {
                        const geprueft = target.getAttribute("data-value") === "1";
                        // Voller Datensatz nötig, siehe removePhoto/uploadFiles:
                        // ACF verlangt required-Felder auch bei Teil-Updates.
                        this.applyItems(await Api.updateItem(id, { ...item, geprueft }));
                        this.render();
                    }
                }
                break;
            case "review-skip":
                if (id != null) {
                    this.reviewQueue = this.reviewQueue.filter((x) => x !== id);
                    this.reviewQueue.push(id);
                    this.render();
                }
                break;
            case "toggle-room-form":
                this.roomFormOpen = !this.roomFormOpen;
                this.render();
                break;
            case "remove-photo":
                await this.removePhoto(target);
                break;
            case "edit-crop":
                this.openCropEditor(Number(target.getAttribute("data-id")));
                break;
            // Fotos und Ausschnitte öffnen dieselbe Lightbox — welches Bild
            // gemeint ist und welche Nachbarn es hat, steht am Element.
            case "view-photo":
            case "view-crop":
                this.openLightboxAt(target);
                break;
            case "set-export-format":
                this.exportFormat = (target.getAttribute("data-format") as App["exportFormat"]) || "pdf";
                this.render();
                break;
            case "set-export-group-items":
                this.exportGroupItems = (target as HTMLInputElement).checked;
                this.render();
                break;
            case "set-export-embed-images":
                this.exportEmbedImages = (target as HTMLInputElement).checked;
                this.render();
                break;
            case "set-export-signature-last":
                this.exportSignatureLastPage = (target as HTMLInputElement).checked;
                this.render();
                break;
            case "set-export-show-alter":
                this.exportShowAlter = (target as HTMLInputElement).checked;
                this.render();
                break;
            case "set-export-intro":
                this.exportIntro = (target as HTMLInputElement).checked;
                this.render();
                break;
            // Der Knopf verwirft einen Text, an dem unter Umstaenden lange
            // gefeilt wurde, und es gibt keinen Weg zurueck — die eigene
            // Fassung ist danach weder im Feld noch im Speicher. Nur fragen,
            // wenn ueberhaupt etwas Eigenes dasteht.
            case "reset-export-intro":
                if (this.exportIntroText.trim() !== DEFAULT_EXPORT_INTRO.trim()) {
                    const ok = await confirmDialog(
                        "Ursprünglichen Text wiederherstellen",
                        "Der bearbeitete Erläuterungstext wird durch die ursprüngliche Fassung ersetzt. Die eigenen Änderungen sind danach nicht mehr wiederherstellbar.",
                        { confirmLabel: "Wiederherstellen" }
                    );
                    if (!ok) break;
                }
                this.exportIntroText = DEFAULT_EXPORT_INTRO;
                writeStored(EXPORT_INTRO_KEY, "");
                this.render();
                break;
            case "run-export": {
                const button = target as HTMLButtonElement;
                const label = button.textContent;
                button.disabled = true;
                button.textContent = "Exportiere …";
                try {
                    await Views.runExport(this);
                } finally {
                    button.disabled = false;
                    button.textContent = label;
                }
                break;
            }
            case "logout":
                window.location.href = jsidea.wp.config.hausinventar?.logoutUrl || "/wp-login.php?action=logout";
                break;
        }
    }

    /**
     * Sammelt alle Bilder der aktuellen Ansicht als Lightbox-Gruppe.
     *
     * Grundlage ist bewusst das DOM und nicht der Datenbestand: So ergibt die
     * Gruppe genau das, was gerade zu sehen ist — in der Positionsansicht also
     * die Fotos der Position samt der Ausschnitte ihrer Gegenstände, in
     * derselben Reihenfolge wie auf dem Bildschirm.
     */
    private lightboxGroup(trigger: HTMLElement): { entries: LightboxEntry[]; index: number } {
        const scope = this.root.querySelector<HTMLElement>("#hi-content") ?? this.root;
        // Nach sichtbarer Lage sortieren statt nach Markup-Reihenfolge: In der
        // Positionsansicht steht das Positionsfoto oben rechts, im Markup aber
        // hinter den Gegenständen. Geblättert wird in Leserichtung.
        const triggers = Array.from(
            scope.querySelectorAll<HTMLElement>('[data-action="view-photo"],[data-action="view-crop"]')
        ).sort((a, b) => {
            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            // Bis zu einer halben Zeilenhöhe Unterschied gilt als dieselbe
            // Zeile — dann entscheidet die Lage von links nach rechts.
            if (Math.abs(ra.top - rb.top) > 24) return ra.top - rb.top;
            return ra.left - rb.left;
        });
        const entries = triggers.map((el): LightboxEntry => {
            const num = (name: string) => Number(el.getAttribute(name));
            const url = el.getAttribute("data-full") || "";
            return {
                url,
                alt: el.getAttribute("data-alt") || "",
                cropSrc: el.getAttribute("data-crop-src") || url,
                crop: el.getAttribute("data-action") === "view-crop"
                    ? { media: 0, x: num("data-x"), y: num("data-y"), w: num("data-w"), h: num("data-h") }
                    : null,
                kind: el.getAttribute("data-lb-kind"),
                id: Number(el.getAttribute("data-lb-id")) || 0,
                media: num("data-media") || 0,
            };
        });
        return { entries, index: Math.max(0, triggers.indexOf(trigger)) };
    }

    /**
     * Öffnet die Lightbox für das angeklickte Bild, eingebettet in die Gruppe
     * aller Bilder der Ansicht.
     *
     * Ausschnitte werden per Canvas aus dem Foto herausgeschnitten, statt sie
     * mit CSS zurechtzuschieben. Dadurch ist das Ergebnis ein ganz normales
     * Bild: es kann nicht verzerren, und Zoom wie Blättern funktionieren für
     * Fotos und Ausschnitte gleich.
     */
    private openLightboxAt(trigger: HTMLElement): void {
        const { entries, index: startIndex } = this.lightboxGroup(trigger);
        if (entries.length === 0) return;
        let index = startIndex;
        let infoOn = readInfoPreference();

        jsidea.modal.show({
            chromeless: true,
            contentPadding: 0,
            className: "hi-lightbox-modal",
            content: (info) => {
                const root = document.createElement("div");
                root.className = "hi-lightbox";

                const viewport = document.createElement("div");
                viewport.className = "hi-lightbox__viewport";
                const img = document.createElement("img");
                viewport.appendChild(img);
                // Ausschnitte der Gegenstände über dem Positionsfoto. Eine
                // eigene Ebene statt Kindern des Bildes: <img> kann keine
                // Kinder haben, und ein umhüllender Rahmen bräche die
                // Größenregeln, mit denen sich das Bild ins Fenster einpasst.
                const marks = document.createElement("div");
                marks.className = "hi-lightbox__marks";
                viewport.appendChild(marks);
                root.appendChild(viewport);

                const panel = document.createElement("div");
                panel.className = "hi-lightbox__info";
                root.appendChild(panel);

                const bar = document.createElement("div");
                bar.className = "hi-lightbox__bar";
                root.appendChild(bar);

                const zoom = this.attachZoom(viewport, img, marks);
                let marksOn = readMarksPreference();

                const button = (iconName: string, label: string, onClick: () => void): HTMLButtonElement => {
                    const b = document.createElement("button");
                    b.type = "button";
                    b.className = "hi-lightbox__btn";
                    b.title = label;
                    b.setAttribute("aria-label", label);
                    b.innerHTML = Views.icon(iconName);
                    b.addEventListener("click", onClick);
                    bar.appendChild(b);
                    return b;
                };
                const separator = () => {
                    const s = document.createElement("span");
                    s.className = "hi-lightbox__sep";
                    bar.appendChild(s);
                };

                // --- Blättern ---
                const prevBtn = button("chevron-left", "Vorheriges Bild", () => show(index - 1));
                const counter = document.createElement("span");
                counter.className = "hi-lightbox__counter";
                bar.appendChild(counter);
                const nextBtn = button("chevron-right", "Nächstes Bild", () => show(index + 1));
                // Bei einem einzelnen Bild wäre Blättern sinnlos.
                const solo = entries.length < 2;
                prevBtn.hidden = solo;
                nextBtn.hidden = solo;
                counter.hidden = solo;
                if (!solo) separator();

                // --- Zoom ---
                button("magnifying-glass-minus", "Verkleinern", () => zoom.zoomBy(1 / 1.4));
                button("arrows-rotate", "Zoom zurücksetzen", () => zoom.reset());
                button("magnifying-glass-plus", "Vergrößern", () => zoom.zoomBy(1.4));
                separator();

                // --- Ausschnitte ---
                const marksBtn = button("vector-square", "Ausschnitte einzeichnen", () => {
                    marksOn = !marksOn;
                    writeMarksPreference(marksOn);
                    drawMarks();
                });

                // --- Daten ---
                const infoBtn = button("circle-info", "Daten einblenden", () => {
                    infoOn = !infoOn;
                    writeInfoPreference(infoOn);
                    applyInfo();
                });

                // --- Original in neuem Tab ---
                const original = document.createElement("a");
                original.className = "hi-lightbox__btn";
                original.target = "_blank";
                original.rel = "noopener noreferrer";
                original.title = "Original in neuem Tab öffnen";
                original.setAttribute("aria-label", "Original in neuem Tab öffnen");
                original.innerHTML = Views.icon("arrow-up-right-from-square");
                bar.appendChild(original);

                separator();
                button("xmark", "Schließen (Esc)", () => info.close());

                const applyInfo = () => {
                    const content = Views.renderLightboxInfo(this, entries[index].kind, entries[index].id);
                    panel.innerHTML = content;
                    // Ohne Daten (etwa bei noch nicht gespeicherten Fotos)
                    // bleibt die Tafel weg, der Knopf wäre wirkungslos.
                    const available = content.length > 0;
                    panel.hidden = !infoOn || !available;
                    infoBtn.disabled = !available;
                    infoBtn.classList.toggle("hi-lightbox__btn--active", infoOn && available);
                    infoBtn.title = infoOn ? "Daten ausblenden" : "Daten einblenden";
                    infoBtn.setAttribute("aria-label", infoBtn.title);
                    infoBtn.setAttribute("aria-pressed", String(infoOn && available));
                };

                /**
                 * Legt die Markierungsebene deckungsgleich auf das Bild.
                 *
                 * Gemessen wird die Lage ohne Zoom (offsetLeft/Width sind von
                 * der Transformation unberührt); den Zoom übernimmt die Ebene
                 * danach über attachZoom. Neu gemessen werden muss, sobald das
                 * Bild geladen ist und wenn sich das Fenster ändert.
                 */
                const placeMarks = () => {
                    marks.style.left = `${img.offsetLeft}px`;
                    marks.style.top = `${img.offsetTop}px`;
                    marks.style.width = `${img.offsetWidth}px`;
                    marks.style.height = `${img.offsetHeight}px`;
                };

                /** Zeichnet die Ausschnitte des aktuellen Positionsfotos. */
                const drawMarks = () => {
                    const entry = entries[index];
                    // Nur auf dem ganzen Positionsfoto: Auf einem Ausschnitt
                    // oder einem Raumfoto gäbe es nichts zuzuordnen.
                    const passend = entry.kind === "position" && !entry.crop && entry.media > 0;
                    const crops = passend ? Views.cropsOnPhoto(this, entry.id, entry.media) : [];

                    marksBtn.hidden = !passend;
                    marksBtn.classList.toggle("hi-lightbox__btn--active", marksOn);
                    marksBtn.setAttribute("aria-pressed", String(marksOn));
                    marksBtn.title = marksOn ? "Ausschnitte ausblenden" : "Ausschnitte einzeichnen";
                    marksBtn.setAttribute("aria-label", marksBtn.title);

                    marks.replaceChildren();
                    marks.hidden = !passend || !marksOn;
                    for (const { item, crop } of crops) {
                        const mark = document.createElement("div");
                        mark.className = "hi-lightbox__mark"
                            + (item.duplikatStatus === "bestaetigt" ? " hi-lightbox__mark--duplicate" : "");
                        mark.style.left = `${crop.x * 100}%`;
                        mark.style.top = `${crop.y * 100}%`;
                        mark.style.width = `${crop.w * 100}%`;
                        mark.style.height = `${crop.h * 100}%`;
                        const label = document.createElement("span");
                        label.className = "hi-lightbox__mark-label";
                        label.textContent = item.anzahl > 1 ? `${item.anzahl}× ${item.title}` : item.title;
                        mark.title = label.textContent;
                        mark.appendChild(label);
                        marks.appendChild(mark);
                    }
                    placeMarks();
                };

                img.addEventListener("load", placeMarks);
                new ResizeObserver(placeMarks).observe(viewport);

                const show = (next: number) => {
                    // Umlaufend: hinter dem letzten Bild kommt wieder das erste.
                    index = (next + entries.length) % entries.length;
                    const entry = entries[index];
                    zoom.reset();
                    img.alt = entry.alt;
                    // Bis ein Zuschnitt berechnet ist, das volle Foto zeigen —
                    // sonst bliebe die Lightbox kurz leer.
                    img.src = entry.url;
                    original.href = entry.url;
                    counter.textContent = `${index + 1} / ${entries.length}`;
                    applyInfo();
                    drawMarks();

                    if (entry.crop) {
                        void Views.cropToDataUrl(entry.cropSrc, entry.crop).then((data) => {
                            // Zwischenzeitliches Weiterblättern nicht überschreiben.
                            if (data && img.isConnected && entries[index] === entry) img.src = data;
                        });
                    }
                };

                /**
                 * Klick auf den Hintergrund schließt die Lightbox.
                 *
                 * Zwei Einschränkungen, ohne die es sich falsch anfühlt:
                 * - Nur die leere Fläche zählt (`root` selbst oder der
                 *   Viewport). Bild, Bedienleiste und Datentafel behalten ihre
                 *   eigenen Klicks; ein Klick aufs Bild darf nicht schließen,
                 *   weil man dort zoomt und schiebt.
                 * - Das Verschieben eines gezoomten Bildes endet ebenfalls mit
                 *   einem `click` — und wer über den Bildrand hinauszieht,
                 *   löst ihn auf dem Viewport aus. Deshalb schließt nur ein
                 *   Klick, bei dem der Zeiger praktisch stehen geblieben ist.
                 */
                let downX = 0;
                let downY = 0;
                root.addEventListener("pointerdown", (e) => {
                    downX = e.clientX;
                    downY = e.clientY;
                });
                root.addEventListener("click", (e) => {
                    const target = e.target as HTMLElement;
                    if (target !== root && target !== viewport) return;
                    if (Math.hypot(e.clientX - downX, e.clientY - downY) > 4) return;
                    info.close();
                });

                // Pfeiltasten zum Blättern; Escape schließt bereits über das Modal.
                root.tabIndex = -1;
                root.addEventListener("keydown", (e) => {
                    if (e.key === "ArrowLeft") show(index - 1);
                    else if (e.key === "ArrowRight") show(index + 1);
                    else return;
                    e.preventDefault();
                });
                window.setTimeout(() => root.focus(), 0);

                show(index);
                return root;
            },
        });
    }

    /**
     * Editor, um per Maus oder Touch das Rechteck aufzuziehen, in dem ein
     * Gegenstand auf einem Positionsfoto liegt. Hat die Position mehrere
     * Fotos, lässt sich zuerst das gemeinte Foto wählen — der Ausschnitt
     * speichert die Attachment-ID mit.
     */
    private openCropEditor(itemId: number): void {
        const item = this.items.find((i) => i.id === itemId);
        if (!item) return;
        const position = this.positions.find((p) => p.id === item.position);
        const fotos = position?.fotos ?? [];
        if (fotos.length === 0) {
            void alertDialog("Kein Foto vorhanden", "Diese Position hat noch kein Foto. Laden Sie zuerst ein Foto hoch, um darauf einen Ausschnitt zu markieren.");
            return;
        }

        // Vorauswahl: das bereits markierte Foto, sonst das erste.
        let activeMedia = fotos.find((f) => f.ID === item.bildausschnitt?.media)?.ID ?? fotos[0].ID;
        let rect: { x: number; y: number; w: number; h: number } | null =
            item.bildausschnitt && item.bildausschnitt.media === activeMedia
                ? { x: item.bildausschnitt.x, y: item.bildausschnitt.y, w: item.bildausschnitt.w, h: item.bildausschnitt.h }
                : null;

        jsidea.modal.show({
            title: "Bildausschnitt markieren",
            className: "hi-crop-modal",
            maxWidth: 900,
            content: (info) => {
                const root = document.createElement("div");
                root.className = "hi-crop-editor";

                const hint = document.createElement("p");
                hint.className = "hi-crop-editor__hint";
                hint.textContent = `Ziehen Sie mit der Maus oder dem Finger ein Rechteck um „${item.title}“. Für kleine Dinge vorher heranzoomen.`;
                root.appendChild(hint);

                // Foto-Auswahl nur zeigen, wenn es überhaupt etwas zu wählen gibt.
                let tabs: HTMLElement | null = null;
                if (fotos.length > 1) {
                    tabs = document.createElement("div");
                    tabs.className = "hi-crop-editor__tabs";
                    root.appendChild(tabs);
                }

                // Zoomleiste über dem Bild. Der Hinweis nennt die Gesten, weil
                // keine davon zu sehen ist.
                const zoomBar = document.createElement("div");
                zoomBar.className = "hi-crop-editor__zoom";
                const zoomButton = (iconName: string, label: string) => {
                    const b = document.createElement("button");
                    b.type = "button";
                    b.className = "hi-btn hi-btn--icon hi-btn--secondary";
                    b.title = label;
                    b.setAttribute("aria-label", label);
                    b.innerHTML = Views.icon(iconName);
                    zoomBar.appendChild(b);
                    return b;
                };
                const zoomOutBtn = zoomButton("magnifying-glass-minus", "Verkleinern");
                const zoomLabel = document.createElement("span");
                zoomLabel.className = "hi-crop-editor__zoom-value";
                zoomBar.appendChild(zoomLabel);
                const zoomInBtn = zoomButton("magnifying-glass-plus", "Vergrößern");
                const fitBtn = zoomButton("compress", "Einpassen");
                const zoomHint = document.createElement("span");
                zoomHint.className = "hi-crop-editor__zoom-hint";
                zoomHint.textContent = "Mausrad zoomt · Leertaste oder mittlere Maustaste + Ziehen verschiebt";
                zoomBar.appendChild(zoomHint);
                root.appendChild(zoomBar);

                // Bühne = scrollbarer Ausschnitt, Rahmen = das Bild in
                // Zoomgröße. Gezoomt wird über die Breite des Rahmens, nicht
                // per CSS-Transformation: So bleibt das Rechteck in Prozent
                // des Rahmens richtig, und relative() misst unverändert am
                // Bild — die Koordinaten stimmen bei jedem Zoomfaktor.
                const stage = document.createElement("div");
                stage.className = "hi-crop-editor__stage";
                const canvas = document.createElement("div");
                canvas.className = "hi-crop-editor__canvas";
                const img = document.createElement("img");
                img.alt = "";
                img.draggable = false;
                const box = document.createElement("div");
                box.className = "hi-crop-editor__box";
                box.hidden = true;
                canvas.appendChild(img);
                canvas.appendChild(box);
                stage.appendChild(canvas);
                root.appendChild(stage);

                const drawBox = () => {
                    if (!rect || rect.w <= 0 || rect.h <= 0) {
                        box.hidden = true;
                        return;
                    }
                    box.hidden = false;
                    box.style.left = rect.x * 100 + "%";
                    box.style.top = rect.y * 100 + "%";
                    box.style.width = rect.w * 100 + "%";
                    box.style.height = rect.h * 100 + "%";
                };

                // --- Zoom ---
                const MIN_SCALE = 1;
                const MAX_SCALE = 6;
                let scale = 1;

                /** Rahmenbreite bei Zoom 1: das ganze Foto passt in die Bühne. */
                const fitWidth = () => {
                    const maxH = window.innerHeight * 0.6;
                    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
                    return Math.max(1, Math.min(stage.clientWidth, maxH * ratio));
                };
                const layout = () => {
                    canvas.style.width = `${fitWidth() * scale}px`;
                    zoomLabel.textContent = `${Math.round(scale * 100)} %`;
                    zoomOutBtn.disabled = scale <= MIN_SCALE;
                    zoomInBtn.disabled = scale >= MAX_SCALE;
                    fitBtn.disabled = scale === MIN_SCALE;
                };

                /**
                 * Zoomt so, dass der Bildpunkt unter (clientX, clientY) an
                 * seiner Stelle bleibt — ohne Koordinaten um die Mitte der
                 * Bühne (für die Knöpfe).
                 */
                const zoomTo = (next: number, clientX?: number, clientY?: number) => {
                    const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
                    if (clamped === scale) return;
                    const s = stage.getBoundingClientRect();
                    const cx = clientX ?? s.left + stage.clientWidth / 2;
                    const cy = clientY ?? s.top + stage.clientHeight / 2;
                    const vorher = img.getBoundingClientRect();
                    const px = (cx - vorher.left) / vorher.width;
                    const py = (cy - vorher.top) / vorher.height;
                    scale = clamped;
                    layout();
                    const nachher = img.getBoundingClientRect();
                    stage.scrollLeft += nachher.left + px * nachher.width - cx;
                    stage.scrollTop += nachher.top + py * nachher.height - cy;
                };

                zoomOutBtn.addEventListener("click", () => zoomTo(scale / 1.5));
                zoomInBtn.addEventListener("click", () => zoomTo(scale * 1.5));
                fitBtn.addEventListener("click", () => zoomTo(MIN_SCALE));
                img.addEventListener("load", layout);
                // Fenster oder Modal ändern ihre Größe: Einpassen neu rechnen.
                new ResizeObserver(layout).observe(stage);

                // Mausrad zoomt um den Mauszeiger: Der Bildpunkt darunter bleibt
                // stehen (zoomTo), wie in der Lightbox. Stufenlos über deltaY,
                // damit ein Trackpad fein und ein Mausrad in Rastern zoomt.
                stage.addEventListener(
                    "wheel",
                    (e) => {
                        e.preventDefault();
                        // Firefox meldet ein Mausrad in Zeilen (deltaMode 1),
                        // die übrigen in Pixeln — auf Pixel umrechnen, sonst
                        // zoomte dort eine Rasterstufe kaum merklich.
                        const pixel = e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 800 : 1);
                        zoomTo(scale * Math.pow(1.0015, -pixel), e.clientX, e.clientY);
                    },
                    { passive: false }
                );

                const renderTabs = () => {
                    if (!tabs) return;
                    tabs.innerHTML = "";
                    for (const f of fotos) {
                        const b = document.createElement("button");
                        b.type = "button";
                        b.className = "hi-crop-editor__tab" + (f.ID === activeMedia ? " hi-crop-editor__tab--active" : "");
                        const t = document.createElement("img");
                        t.src = f.sizes?.thumbnail || f.url;
                        t.alt = "";
                        b.appendChild(t);
                        b.addEventListener("click", () => {
                            if (activeMedia === f.ID) return;
                            activeMedia = f.ID;
                            // Ein Rechteck gilt immer für genau ein Foto —
                            // beim Wechsel wird es daher verworfen.
                            rect = null;
                            scale = MIN_SCALE;
                            img.src = f.url;
                            drawBox();
                            renderTabs();
                            updateButtons();
                        });
                        tabs.appendChild(b);
                    }
                };

                img.src = fotos.find((f) => f.ID === activeMedia)?.url || fotos[0].url;

                // --- Zeiger: Rechteck aufziehen, verschieben, mit zwei Fingern zoomen ---
                //
                // Pointer-Events decken Maus, Stift und Touch gemeinsam ab;
                // touch-action:none (im SCSS) verhindert, dass die Geste
                // stattdessen die Seite scrollt. Gelauscht wird am Rahmen, nicht
                // an der Bühne: Ein Griff an die Scrollleiste soll scrollen und
                // kein Rechteck beginnen.
                const pointers = new Map<number, { x: number; y: number }>();
                let mode: "none" | "draw" | "pan" | "pinch" = "none";
                let startX = 0;
                let startY = 0;
                let rectBefore: typeof rect = null;
                let panStart = { x: 0, y: 0, left: 0, top: 0 };
                let pinchStart = { dist: 1, scale: 1 };
                let spaceDown = false;

                const relative = (e: PointerEvent) => {
                    const b = img.getBoundingClientRect();
                    return {
                        x: Math.min(1, Math.max(0, (e.clientX - b.left) / b.width)),
                        y: Math.min(1, Math.max(0, (e.clientY - b.top) / b.height)),
                    };
                };
                const pinchGeometry = () => {
                    const [a, b] = Array.from(pointers.values());
                    return {
                        dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
                        x: (a.x + b.x) / 2,
                        y: (a.y + b.y) / 2,
                    };
                };
                const setPanCursor = () => {
                    canvas.classList.toggle("hi-crop-editor__canvas--pan", spaceDown || mode === "pan");
                };

                canvas.addEventListener("pointerdown", (e) => {
                    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
                    canvas.setPointerCapture(e.pointerId);
                    e.preventDefault();

                    if (pointers.size === 2) {
                        // Zweiter Finger: Das war kein Rechteck, sondern der
                        // Beginn einer Zoomgeste — das angefangene verwerfen.
                        if (mode === "draw") {
                            rect = rectBefore;
                            drawBox();
                        }
                        mode = "pinch";
                        pinchStart = { dist: pinchGeometry().dist, scale };
                        return;
                    }
                    if (pointers.size > 2) return;

                    if (spaceDown || e.button === 1) {
                        mode = "pan";
                        panStart = { x: e.clientX, y: e.clientY, left: stage.scrollLeft, top: stage.scrollTop };
                        setPanCursor();
                        return;
                    }
                    if (e.button !== undefined && e.button !== 0) return;

                    mode = "draw";
                    rectBefore = rect;
                    const p = relative(e);
                    startX = p.x;
                    startY = p.y;
                    rect = { x: p.x, y: p.y, w: 0, h: 0 };
                    drawBox();
                });
                canvas.addEventListener("pointermove", (e) => {
                    if (!pointers.has(e.pointerId)) return;
                    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

                    if (mode === "pinch" && pointers.size >= 2) {
                        const g = pinchGeometry();
                        zoomTo(pinchStart.scale * (g.dist / pinchStart.dist), g.x, g.y);
                    } else if (mode === "pan") {
                        stage.scrollLeft = panStart.left - (e.clientX - panStart.x);
                        stage.scrollTop = panStart.top - (e.clientY - panStart.y);
                    } else if (mode === "draw") {
                        const p = relative(e);
                        rect = {
                            x: Math.min(startX, p.x),
                            y: Math.min(startY, p.y),
                            w: Math.abs(p.x - startX),
                            h: Math.abs(p.y - startY),
                        };
                        drawBox();
                    }
                });
                const endPointer = (e: PointerEvent) => {
                    if (!pointers.delete(e.pointerId)) return;
                    if (mode === "draw") {
                        // Ein Tap ohne echte Ziehbewegung ist keine Auswahl,
                        // sondern würde ein 0×0-Rechteck speichern.
                        if (rect && (rect.w < 0.01 || rect.h < 0.01)) rect = rectBefore;
                        drawBox();
                        updateButtons();
                    }
                    // Nach einer Zoomgeste beginnt der verbleibende Finger kein
                    // Rechteck — erst ein neues Aufsetzen tut das.
                    if (pointers.size === 0 || mode !== "pinch") mode = "none";
                    setPanCursor();
                };
                canvas.addEventListener("pointerup", endPointer);
                canvas.addEventListener("pointercancel", endPointer);

                // Leertaste gedrückt halten = verschieben, wie in Bildprogrammen.
                // Am Modal selbst gelauscht (es bekommt beim Klick ins Bild den
                // Fokus), damit die Taste keinen fokussierten Knopf auslöst.
                root.tabIndex = -1;
                root.addEventListener("keydown", (e) => {
                    if (e.code !== "Space" || e.target !== root) return;
                    e.preventDefault();
                    spaceDown = true;
                    setPanCursor();
                });
                root.addEventListener("keyup", (e) => {
                    if (e.code !== "Space") return;
                    spaceDown = false;
                    setPanCursor();
                });
                window.setTimeout(() => root.focus(), 0);

                const actions = document.createElement("div");
                actions.className = "hi-crop-editor__actions";
                const removeBtn = document.createElement("button");
                removeBtn.type = "button";
                removeBtn.className = "hi-btn hi-btn--secondary";
                removeBtn.textContent = "Ausschnitt entfernen";
                const cancelBtn = document.createElement("button");
                cancelBtn.type = "button";
                cancelBtn.className = "hi-btn hi-btn--ghost";
                cancelBtn.textContent = "Abbrechen";
                const saveBtn = document.createElement("button");
                saveBtn.type = "button";
                saveBtn.className = "hi-btn";
                saveBtn.textContent = "Speichern";
                actions.append(removeBtn, cancelBtn, saveBtn);
                root.appendChild(actions);

                const updateButtons = () => {
                    saveBtn.disabled = !rect;
                    removeBtn.hidden = !item.bildausschnitt;
                };

                cancelBtn.addEventListener("click", () => info.close());
                // Der Knopf steht direkt neben „Abbrechen" und loescht einen
                // von Hand gezogenen Ausschnitt unwiderruflich — er laesst sich
                // nur durch erneutes Aufziehen wiederherstellen. Deshalb fragen.
                removeBtn.addEventListener("click", () => {
                    void (async () => {
                        const ok = await confirmDialog(
                            "Bildausschnitt entfernen",
                            `Der Bildausschnitt von „${item.title}“ wird gelöscht. In der Liste steht der Gegenstand danach ohne Bild, bis ein neuer Ausschnitt aufgezogen wird.`,
                            { confirmLabel: "Ausschnitt entfernen" }
                        );
                        if (!ok) return;
                        info.close();
                        await this.saveCrop(itemId, null);
                    })();
                });
                saveBtn.addEventListener("click", () => {
                    if (!rect) return;
                    const crop: ItemCrop = { media: activeMedia, x: rect.x, y: rect.y, w: rect.w, h: rect.h };
                    info.close();
                    void this.saveCrop(itemId, crop);
                });

                renderTabs();
                drawBox();
                updateButtons();
                return root;
            },
        });
    }

    private async saveCrop(itemId: number, crop: ItemCrop | null): Promise<void> {
        const item = this.items.find((i) => i.id === itemId);
        if (!item) return;
        try {
            // ACF verlangt bei required-Feldern den vollen Datensatz —
            // daher wie beim Foto-Entfernen das ganze Item mitschicken.
            this.applyItems(await Api.updateItem(itemId, { ...item, bildausschnitt: crop }));
            this.render();
        } catch (error) {
            await alertDialog("Speichern fehlgeschlagen", error instanceof Error ? error.message : String(error));
        }
    }

    /**
     * Zoomen der Lightbox per Mausrad, Pinch-Geste (Touch) und
     * Doppelklick/-tap zum Zurücksetzen.
     *
     * Gibt eine Steuerung zurück, damit auch die Bedienleiste zoomen und beim
     * Blättern auf das nächste Bild zurücksetzen kann.
     */
    private attachZoom(container: HTMLElement, img: HTMLImageElement, follower?: HTMLElement): ZoomControl {
        const minScale = 1;
        const maxScale = 4;
        let scale = 1;
        let tx = 0;
        let ty = 0;

        const apply = () => {
            img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
            // Liegt genau über dem Bild und dreht um dieselbe Mitte — mit
            // derselben Transformation bleiben die Markierungen beim Zoomen
            // und Schieben auf ihrer Stelle im Foto.
            if (follower) follower.style.transform = img.style.transform;
            img.style.cursor = scale > 1 ? "grab" : "";
        };

        /**
         * Zoomt so, dass der Punkt unter dem Zeiger stehen bleibt.
         *
         * Die Transformation ist `translate(t) scale(s)` um die Bildmitte M.
         * Ein Bildpunkt landet bei `M + t + s·p`. Damit der Punkt unter dem
         * Zeiger (Position z) nach dem Skalieren an derselben Stelle liegt,
         * muss die Verschiebung um den Anteil mitwandern, um den sich sein
         * Abstand zur Mitte ändert:  t' = t + (z − M) · (1 − s'/s).
         */
        const zoomAt = (next: number, clientX?: number, clientY?: number) => {
            const clamped = Math.min(maxScale, Math.max(minScale, next));
            const k = clamped / scale;
            if (k !== 1 && clientX !== undefined && clientY !== undefined) {
                // Mitte des aktuell dargestellten (bereits transformierten) Bildes.
                const r = img.getBoundingClientRect();
                tx += (clientX - (r.left + r.width / 2)) * (1 - k);
                ty += (clientY - (r.top + r.height / 2)) * (1 - k);
            }
            scale = clamped;
            // Ganz herausgezoomt wieder mittig einpassen.
            if (scale === minScale) {
                tx = 0;
                ty = 0;
            }
            apply();
        };

        container.addEventListener(
            "wheel",
            (e) => {
                e.preventDefault();
                zoomAt(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
            },
            { passive: false }
        );

        let dragging = false;
        let startX = 0;
        let startY = 0;
        let startTx = 0;
        let startTy = 0;
        container.addEventListener("pointerdown", (e) => {
            if (scale <= 1) return;
            dragging = true;
            startX = e.clientX;
            startY = e.clientY;
            startTx = tx;
            startTy = ty;
            container.setPointerCapture(e.pointerId);
        });
        container.addEventListener("pointermove", (e) => {
            if (!dragging) return;
            tx = startTx + (e.clientX - startX);
            ty = startTy + (e.clientY - startY);
            apply();
        });
        const endDrag = () => {
            dragging = false;
        };
        container.addEventListener("pointerup", endDrag);
        container.addEventListener("pointercancel", endDrag);

        let pinchStartDist = 0;
        let pinchStartScale = 1;
        container.addEventListener(
            "touchstart",
            (e) => {
                if (e.touches.length === 2) {
                    pinchStartDist = touchDistance(e.touches);
                    pinchStartScale = scale;
                }
            },
            { passive: true }
        );
        container.addEventListener(
            "touchmove",
            (e) => {
                if (e.touches.length === 2 && pinchStartDist > 0) {
                    e.preventDefault();
                    // Zoom-Zentrum ist die Mitte zwischen den Fingern.
                    const c = touchCenter(e.touches);
                    zoomAt(pinchStartScale * (touchDistance(e.touches) / pinchStartDist), c.x, c.y);
                }
            },
            { passive: false }
        );

        // Doppelklick zoomt auf die angeklickte Stelle, nicht auf die Mitte.
        container.addEventListener("dblclick", (e) => zoomAt(scale > 1 ? 1 : 2, e.clientX, e.clientY));

        return {
            // Ohne Koordinaten zoomt zoomAt um die Bildmitte — genau richtig
            // für die Knöpfe in der Bedienleiste.
            zoomBy: (factor) => zoomAt(scale * factor),
            reset: () => zoomAt(minScale),
            scale: () => scale,
            min: minScale,
            max: maxScale,
        };
    }

    private async removePhoto(target: HTMLElement): Promise<void> {
        const owner = target.getAttribute("data-owner");
        const ownerId = Number(target.getAttribute("data-id"));
        const photoId = Number(target.getAttribute("data-photo-id"));

        if (owner === "new-item") {
            this.newItemPhotos = this.newItemPhotos.filter((f) => f.ID !== photoId);
            this.render();
            return;
        }
        if (owner === "new-position") {
            this.newPositionPhotos = this.newPositionPhotos.filter((f) => f.ID !== photoId);
            this.render();
            return;
        }

        // Ab hier wird an einem gespeicherten Datensatz gearbeitet — und bei
        // einer Position hängt mehr am Foto als das Foto selbst: Jeder
        // Bildausschnitt, der darauf zeigt, wird mitgelöscht (clearCropsForPhoto)
        // und muss danach von Hand neu gezogen werden. Deshalb wird gefragt,
        // und der Dialog benennt jeden betroffenen Gegenstand einzeln — sonst
        // ist die Tragweite erst nach dem Klick sichtbar.
        const verloren: string[] = [];
        if (owner === "position") {
            const betroffen = this.items.filter(
                (i) => i.position === ownerId && i.bildausschnitt?.media === photoId
            );
            for (const item of betroffen.slice(0, 8)) {
                verloren.push(`Bildausschnitt von „${item.title}“`);
            }
            if (betroffen.length > 8) {
                verloren.push(`… und ${betroffen.length - 8} weitere Bildausschnitte`);
            }
        }

        const ok = await confirmDialog(
            "Foto entfernen",
            verloren.length > 0
                ? "Das Foto wird von dieser Position gelöst; in der Mediathek bleibt es erhalten. Unwiderruflich verloren geht dabei:"
                : "Das Foto wird von diesem Datensatz gelöst. In der Mediathek bleibt es erhalten.",
            { confirmLabel: "Foto entfernen", details: verloren }
        );
        if (!ok) return;

        if (owner === "item") {
            const item = this.items.find((i) => i.id === ownerId);
            if (!item) return;
            // ACF verlangt bei required-Feldern (anzahl, wert) auch bei
            // Teil-Updates den vollständigen Datensatz — sonst schlägt die
            // REST-Validierung fehl, obwohl nur die Fotos geändert werden.
            this.applyItems(await Api.updateItem(ownerId, { ...item, fotos: item.fotos.filter((f) => f.ID !== photoId) }));
        } else if (owner === "position") {
            const position = this.positions.find((p) => p.id === ownerId);
            if (!position) return;
            this.applyPosition(await Api.updatePosition(ownerId, { ...position, fotos: position.fotos.filter((f) => f.ID !== photoId) }));
            // Bildausschnitte, die auf dieses Foto zeigen, würden sonst als
            // Verweis auf ein nicht mehr vorhandenes Bild zurückbleiben.
            await this.clearCropsForPhoto(ownerId, photoId);
        } else if (owner === "room") {
            const room = this.rooms.find((r) => r.id === ownerId);
            if (!room) return;
            this.applyRoom(await Api.updateRoom(ownerId, { fotos: room.fotos.filter((f) => f.ID !== photoId) }));
        }
        this.render();
    }

    /**
     * Entfernt die Bildausschnitte aller Gegenstände einer Position, die
     * sich auf ein gerade gelöschtes Foto beziehen.
     */
    private async clearCropsForPhoto(positionId: number, photoId: number): Promise<void> {
        const affected = this.items.filter((i) => i.position === positionId && i.bildausschnitt?.media === photoId);
        if (affected.length === 0) return;
        this.applyItems(...await inParallel(affected, 4, (item) => Api.updateItem(item.id, { ...item, bildausschnitt: null })));
    }

    /**
     * Löscht eine Position samt allem, was daran hängt: ihre Gegenstände und
     * sämtliche Fotos aus der Mediathek.
     *
     * Früher verweigerte die App das Löschen, solange noch Gegenstände an der
     * Position hingen. Jetzt wird durchgelöscht — deshalb muss der Dialog
     * jeden betroffenen Posten einzeln benennen, damit die Tragweite vor dem
     * Klick sichtbar ist und nicht erst danach.
     *
     * WordPress räumt die Anhänge eines gelöschten Beitrags nicht mit weg,
     * die Fotos werden also ausdrücklich einzeln entfernt.
     */
    private async deletePositionCascade(id: number): Promise<void> {
        const position = this.positions.find((p) => p.id === id);
        if (!position) return;

        const children = this.items.filter((i) => i.position === id);
        const stueck = children.reduce((sum, i) => sum + i.anzahl, 0);
        const mediaIds = Array.from(
            new Set([
                ...position.fotos.map((f) => f.ID),
                ...children.flatMap((i) => i.fotos.map((f) => f.ID)),
            ])
        );

        const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
        const details = [`die Position „${position.title}“ mit ihrer Beschreibung`];
        if (children.length > 0) {
            details.push(
                `${plural(children.length, "Gegenstand", "Gegenstände")} (${plural(stueck, "Einzelstück", "Einzelstücke")}) mit allen erfassten Werten`
            );
        }
        if (mediaIds.length > 0) {
            details.push(`${plural(mediaIds.length, "Foto", "Fotos")} — auch aus der Mediathek`);
        }

        const ok = await confirmDialog(
            "Position mit allen Inhalten löschen",
            `Beim Löschen dieser Position wird Folgendes unwiderruflich mit entfernt:`,
            { confirmLabel: "Endgültig löschen", details }
        );
        if (!ok) return;

        // Reihenfolge bewusst: erst die Kinder, dann die Dateien, zuletzt die
        // Position. Bricht etwas ab, bleibt die Position bestehen und der
        // Vorgang ist wiederholbar, statt einen verwaisten Rest zu hinterlassen.
        try {
            await inParallel(children, 4, (child) => Api.deleteItem(child.id));
            if (mediaIds.length > 0) {
                await Api.deleteMedia(mediaIds);
            }
            await Api.deletePosition(id);
        } catch (error) {
            // Mittendrin abgebrochen: Was schon weg ist, lässt sich nicht
            // lokal nachrechnen — einmal vollständig neu laden.
            await this.reload();
            this.render();
            throw error;
        }
        this.dropPosition(id);
        this.setView("positions");
    }

    private handleUpload(input: HTMLInputElement): void {
        const owner = input.getAttribute("data-upload");
        const ownerId = Number(input.getAttribute("data-id"));
        const files = input.files ? Array.from(input.files) : [];
        if (owner) void this.uploadFiles(owner, ownerId, files);
        // Zurücksetzen, damit dieselbe Datei erneut ausgewählt werden kann.
        input.value = "";
    }

    /** Schnelles Ändern von Anzahl, Wert und Sonstiges direkt in der Gegenstands-Zeile, ohne die volle Bearbeiten-Ansicht zu öffnen. */
    private async handleQuickEdit(input: HTMLInputElement | HTMLTextAreaElement): Promise<void> {
        const field = input.getAttribute("data-quick-edit");
        const id = Number(input.getAttribute("data-id"));
        const item = this.items.find((i) => i.id === id);
        if (!item || !field) return;

        try {
            if (field === "anzahl") {
                const anzahl = Math.max(1, Math.round(Number(input.value)) || 1);
                this.applyItems(await Api.updateItem(id, { ...item, anzahl }));
            } else if (field === "wert") {
                const wert = Number(String(input.value).trim().replace(",", "."));
                this.applyItems(await Api.updateItem(id, { ...item, wert: Number.isFinite(wert) ? wert : item.wert }));
            } else if (field === "sonstiges") {
                const sonstiges = input.value.trim();
                // Unveränderter Text: Ein Klick ins Feld und wieder heraus soll
                // weder speichern noch die Ansicht neu aufbauen.
                if (sonstiges === item.sonstiges) return;
                this.applyItems(await Api.updateItem(id, { ...item, sonstiges }));
            }
            this.render();
        } catch (error) {
            await alertDialog("Speichern fehlgeschlagen", error instanceof Error ? error.message : String(error));
            await this.reload();
            this.render();
        }
    }

    private async uploadFiles(owner: string, ownerId: number, files: File[]): Promise<void> {
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length === 0) return;

        const progressEl = document.getElementById(Views.uploadProgressId(owner, ownerId));
        const fill = progressEl?.querySelector<HTMLElement>(".hi-dropzone__progress-fill");
        const label = progressEl?.querySelector<HTMLElement>(".hi-dropzone__progress-label");
        const totalBytes = imageFiles.reduce((s, f) => s + f.size, 0);
        const loadedPerFile = new Array(imageFiles.length).fill(0);

        const updateProgress = () => {
            const loaded = loadedPerFile.reduce((s, v) => s + v, 0);
            // Erst einblenden, sobald tatsächlich Bytes unterwegs sind —
            // nicht schon vorab bei 0%, sonst blitzt die Anzeige nur kurz auf.
            if (loaded > 0) {
                progressEl?.removeAttribute("hidden");
            }
            const pct = totalBytes > 0 ? Math.min(100, Math.round((loaded / totalBytes) * 100)) : 0;
            if (fill) fill.style.width = pct + "%";
            if (label) label.textContent = pct + "%";
        };

        // Bei "new-*" existiert der Beitrag noch nicht — die Zuordnung
        // "Hochgeladen zu" wird dann nach dem Speichern nachgetragen.
        const attachTo = owner === "new-item" || owner === "new-position" ? null : ownerId;

        const uploaded: GalleryImage[] = [];
        try {
            for (let i = 0; i < imageFiles.length; i++) {
                const file = imageFiles[i];
                uploaded.push(await Api.uploadMedia(file, attachTo, (loadedBytes) => {
                    loadedPerFile[i] = loadedBytes;
                    updateProgress();
                }));
            }
        } finally {
            progressEl?.setAttribute("hidden", "");
            if (fill) fill.style.width = "0%";
            if (label) label.textContent = "0%";
        }

        if (owner === "new-item") {
            this.newItemPhotos = [...this.newItemPhotos, ...uploaded];
            this.render();
            return;
        }
        if (owner === "new-position") {
            this.newPositionPhotos = [...this.newPositionPhotos, ...uploaded];
            this.render();
            return;
        }
        if (owner === "item") {
            const item = this.items.find((i) => i.id === ownerId);
            if (item) this.applyItems(await Api.updateItem(ownerId, { ...item, fotos: [...item.fotos, ...uploaded] }));
        } else if (owner === "position") {
            const position = this.positions.find((p) => p.id === ownerId);
            if (position) this.applyPosition(await Api.updatePosition(ownerId, { ...position, fotos: [...position.fotos, ...uploaded] }));
        } else if (owner === "room") {
            const room = this.rooms.find((r) => r.id === ownerId);
            if (room) this.applyRoom(await Api.updateRoom(ownerId, { fotos: [...room.fotos, ...uploaded] }));
        }
        this.render();
    }

    /**
     * Löscht den gesamten Bestand (Gefahrenzone unter „KI-Zugriff").
     *
     * Die Freigabe kommt vom Server über `canReset` und gilt nur auf der
     * Entwicklungsmaschine; hier wird sie erneut geprüft, damit ein
     * abgeschicktes Formular aus einem alten Tab nichts anrichtet.
     *
     * Das getippte Wort wird ohne Rücksicht auf Groß- und Kleinschreibung
     * verglichen — die Hürde soll bewusstes Bestätigen erzwingen, nicht die
     * Feststelltaste.
     */
    private async handleResetAll(bestaetigung: string): Promise<void> {
        if (!this.canReset) return;

        if (bestaetigung.toLocaleUpperCase("de-DE") !== "LÖSCHEN") {
            await alertDialog(
                "Nicht gelöscht",
                'Zum Bestätigen muss das Wort „LÖSCHEN" eingetippt werden. Es wurde nichts verändert.',
            );
            return;
        }

        this.resetProgress = "Bestand wird ermittelt …";
        this.render();
        try {
            await deleteEverything((done, total, label) => {
                this.resetProgress = total === 0
                    ? "Nichts zu löschen."
                    : `${label}: ${done} von ${total} gelöscht …`;
                // Kein volles render(): Der Rundumschlag läuft über tausend
                // Anfragen, ein kompletter Neuaufbau je Welle wäre Verschwendung.
                // Nur der Textknoten wird getauscht — das Spinner-SVG daneben
                // soll weiterlaufen und nicht bei jeder Welle neu anfangen.
                const anzeige = this.root.querySelector(".hi-danger__progress-text");
                if (anzeige) anzeige.textContent = this.resetProgress;
            });
        } catch (error) {
            await alertDialog("Löschen fehlgeschlagen", error instanceof Error ? error.message : String(error));
        } finally {
            this.resetProgress = null;
            await this.reload();
            this.render();
        }
    }

    private async handleSubmit(event: SubmitEvent, form: HTMLFormElement): Promise<void> {
        event.preventDefault();
        const kind = form.getAttribute("data-form");
        const submitter = event.submitter as HTMLElement | null;
        const data = new FormData(form);
        const str = (name: string) => String(data.get(name) ?? "").trim();
        // Zahlenfelder wie "Wert" sind type="text", damit deutsche
        // Komma-Eingaben ("12,50") nicht durch die native
        // Zahl-Validierung von <input type="number"> abgefangen werden
        // (die nur den Punkt als Trenner akzeptiert und bei ungültigem
        // Wert die gesamte Formular-Übermittlung stillschweigend blockiert).
        const num = (name: string) => Number(String(data.get(name) ?? "0").trim().replace(",", "."));
        const shopLinks = () => readShopLinks(data);
        const dupRefs = () => readDuplicateRefs(data);

        try {
            // Der Wartezustand sitzt am gedrückten Knopf — bei mehreren
            // Submit-Knöpfen (etwa "Speichern" und "Als geprüft bestätigen")
            // also genau an dem, den der Nutzer gewählt hat.
            await withBusy(submitter, () => this.handleFormSubmit(kind, form, str, num, shopLinks, dupRefs, submitter));
        } catch (error) {
            await alertDialog("Speichern fehlgeschlagen", error instanceof Error ? error.message : String(error));
        }
    }

    private async handleFormSubmit(
        kind: string | null,
        form: HTMLFormElement,
        str: (name: string) => string,
        num: (name: string) => number,
        shopLinks: () => ShopLink[],
        dupRefs: () => DuplicateRef[],
        submitter: HTMLElement | null
    ): Promise<void> {
        switch (kind) {
            case "compare-reference": {
                await this.saveReference(form);
                break;
            }
            case "mcp-create": {
                const created = await Api.createMcpCredential(str("name"));
                this.mcpCredentials = [...(this.mcpCredentials ?? []), created];
                this.render();
                break;
            }
            case "reset-all": {
                await this.handleResetAll(str("bestaetigung"));
                break;
            }
            case "position-create": {
                const created = await Api.createPosition({
                    title: str("title"),
                    nummer: num("nummer"),
                    raum: num("raum"),
                    beschreibung: str("beschreibung"),
                    fotos: this.newPositionPhotos,
                });
                await Api.attachMediaToPost(this.newPositionPhotos.map((f) => f.ID), created.id);
                this.newPositionPhotos = [];
                this.applyPosition(created);
                this.setView("position", created.id);
                break;
            }
            case "position-edit": {
                const id = Number(form.getAttribute("data-id"));
                this.applyPosition(await Api.updatePosition(id, {
                    title: str("title"),
                    nummer: num("nummer"),
                    raum: num("raum"),
                    beschreibung: str("beschreibung"),
                }, this.positions.find((p) => p.id === id)?.fotos));
                this.setView("position", id);
                break;
            }
            case "item-create": {
                const positionId = this.selectedId;
                if (positionId == null) break;
                const siblingCount = this.items.filter((i) => i.position === positionId).length;
                const createdItem = await Api.createItem({
                    position: positionId,
                    order: siblingCount,
                    title: str("title"),
                    anzahl: num("anzahl") || 1,
                    alter: str("alter"),
                    wert: num("wert"),
                    versandkosten: num("versandkosten"),
                    datum: str("datum"),
                    quelle: str("quelle"),
                    shopLinks: shopLinks(),
                    wertBegruendung: str("wert_begruendung"),
                    sonstiges: str("sonstiges"),
                    fotos: this.newItemPhotos,
                });
                await Api.attachMediaToPost(this.newItemPhotos.map((f) => f.ID), createdItem.id);
                this.newItemPhotos = [];
                this.addingItem = false;
                this.applyItems(createdItem);
                this.render();
                break;
            }
            case "note-save": {
                const attr = form.getAttribute("data-id");
                const daten = {
                    title: str("title"),
                    level: (str("wichtigkeit") || "hinweis") as NoteLevel,
                    text: str("text"),
                    imageKey: str("image_key"),
                    // Die Auswahllisten liefern "0" für "kein Bezug";
                    // Api.noteFields macht daraus null, sonst bliebe eine 0 als
                    // Post-ID stehen.
                    raum: num("raum"),
                    position: num("position"),
                    item: num("item"),
                    erledigt: !!form.querySelector<HTMLInputElement>('[name="erledigt"]')?.checked,
                };
                this.applyNote(attr
                    ? await Api.updateNote(Number(attr), daten)
                    : await Api.createNote({ ...daten, order: this.notes.length }));
                this.editingNoteId = null;
                this.render();
                break;
            }
            case "item-edit": {
                const id = Number(form.getAttribute("data-id"));
                this.applyItems(await Api.updateItem(id, {
                    title: str("title"),
                    anzahl: num("anzahl") || 1,
                    alter: str("alter"),
                    wert: num("wert"),
                    versandkosten: num("versandkosten"),
                    datum: str("datum"),
                    quelle: str("quelle"),
                    shopLinks: shopLinks(),
                    wertBegruendung: str("wert_begruendung"),
                    sonstiges: str("sonstiges"),
                    duplikatStatus: str("duplikat_status") as DuplicateStatus,
                    duplikatRefs: dupRefs(),
                }, this.items.find((i) => i.id === id)?.fotos));
                this.editingItemId = null;
                this.render();
                break;
            }
            case "review-save": {
                const id = Number(form.getAttribute("data-id"));
                const confirm = submitter?.getAttribute("data-intent") === "confirm";
                this.applyItems(await Api.updateItem(id, {
                    title: str("title"),
                    anzahl: num("anzahl") || 1,
                    alter: str("alter"),
                    wert: num("wert"),
                    versandkosten: num("versandkosten"),
                    datum: str("datum"),
                    quelle: str("quelle"),
                    shopLinks: shopLinks(),
                    wertBegruendung: str("wert_begruendung"),
                    sonstiges: str("sonstiges"),
                    geprueft: confirm ? true : undefined,
                }, this.items.find((i) => i.id === id)?.fotos));
                if (confirm) {
                    this.reviewQueue = this.reviewQueue.filter((x) => x !== id);
                }
                this.render();
                break;
            }
            case "room-create": {
                this.applyRoom(await Api.createRoom({ title: str("title"), order: this.rooms.length, beschreibung: "" }));
                this.roomFormOpen = false;
                this.render();
                break;
            }
            case "room-edit": {
                const id = Number(form.getAttribute("data-id"));
                this.applyRoom(await Api.updateRoom(id, { title: str("title"), beschreibung: str("beschreibung") }, this.rooms.find((r) => r.id === id)?.fotos));
                this.setView("room", id);
                break;
            }
        }
    }
}

jsidea.ready(() => {
    const root = document.getElementById("hausinventar-app");
    if (!root) return;
    new App(root).boot();
});
